import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { movimientosAPI, clientesAPI, tiposClientesAPI, movimientosPredefinidosAPI, formasPagoAPI, recibosCobroAPI, pagosAPI, bancosAPI, cuentasBancariasAPI, proveedoresAPI, fondosAPI, cajasAPI, cotizacionesAPI, withSaldoNegativoConfirm } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';
import '../MovimientosFondos.css';
import { tituloMovimientoFondos, subtituloMovimientoFondos, etiquetaClaseFondo } from '../utils/fondosLabels';
import { aplicarBnaALinea, aplicarCambioMontoUsd, textoTasaUsada } from '../utils/montoDolarFormaPago';

const MESES_NOMBRES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const PREDEFINIDOS_SISTEMA = new Set(['Anulación Recibo', 'Anulación Orden de Pago', 'Anulación Pago a cuenta']);

const esPredefinidoOrdenPago = (p) => (p?.descripcion || '').trim() === 'Orden de Pago';
const esPredefinidoPagoCuenta = (p) => (p?.descripcion || '').trim() === 'Pago a cuenta';
const esPredefinidoPagoComprobante = (p) => esPredefinidoOrdenPago(p) || esPredefinidoPagoCuenta(p);

function redondearCentavos(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function usdDesdePesos(pesos, cotizacion) {
  const c = Number(cotizacion);
  if (!(c > 0)) return null;
  return redondearCentavos(Number(pesos) / c);
}

function pesosDesdeUsd(usd, cotizacion) {
  const c = Number(cotizacion);
  if (!(c > 0)) return null;
  return redondearCentavos(Number(usd) * c);
}

function esContextoProveedores(locationState, predefinido, proveedoresSeleccionados) {
  if (predefinido?.uso === 'proveedores') return true;
  return Boolean(
    locationState?.nuevoPago
    || locationState?.modoProveedores
    || (locationState?.proveedorId && proveedoresSeleccionados?.length > 0)
  );
}

function filtrarPredefinidosFormulario(lista) {
  if (!Array.isArray(lista)) return [];
  return lista.filter(
    (m) =>
      m.seleccionable_en_formulario !== 0 &&
      m.seleccionable_en_formulario !== false &&
      !PREDEFINIDOS_SISTEMA.has(m.descripcion)
  );
}

const MovimientoForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, filtrarPredefinidosPermitidos } = useAuth();
  const { fechaHoy, formatFecha } = useZonaHoraria();
  const [loading, setLoading] = useState(false);
  const [modoMasivo, setModoMasivo] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [movimientosPredefinidos, setMovimientosPredefinidos] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [clientesSeleccionados, setClientesSeleccionados] = useState([]);
  const [tipoMovimientoSeleccionado, setTipoMovimientoSeleccionado] = useState(null);
  const [movimientoPredefinidoSeleccionado, setMovimientoPredefinidoSeleccionado] = useState(null);
  const [formasPagoRecibo, setFormasPagoRecibo] = useState([]); // Array de formas de pago para el recibo
  const [cotizacionBna, setCotizacionBna] = useState(null);
  const [cotizacionManual, setCotizacionManual] = useState('');
  const [cotizacionError, setCotizacionError] = useState(false);
  const [formData, setFormData] = useState({
    tipo_id: '',
    monto: '',
    concepto: '',
    fecha: '',
    forma_pago_id: '',
    mes: '',
    anio: ''
  });
  const [abonoPermitirDuplicado, setAbonoPermitirDuplicado] = useState(false);
  const [abonoAclaracion, setAbonoAclaracion] = useState('');
  const [filtrosLista, setFiltrosLista] = useState({
    busqueda_cliente: '',
    busqueda_proveedor: '',
    cuit: ''
  });
  const [, setCarteraCheques] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [proveedoresSeleccionados, setProveedoresSeleccionados] = useState([]);
  const [modalCarteraOpen, setModalCarteraOpen] = useState(false);
  const [modalCarteraIndex, setModalCarteraIndex] = useState(null);
  const [carteraRecibidos, setCarteraRecibidos] = useState([]);
  const [carteraSeleccionados, setCarteraSeleccionados] = useState([]);
  const [carteraModalSortBy, setCarteraModalSortBy] = useState('fecha_cobro');
  const [carteraModalSortAsc, setCarteraModalSortAsc] = useState(true);
  const preseleccionReciboAplicada = useRef(false);
  const saldoInicialReciboAplicado = useRef(false);
  const [esModoFondos, setEsModoFondos] = useState(false);
  const [predefinidosFondos, setPredefinidosFondos] = useState([]);
  const [predefinidoFondosSeleccionado, setPredefinidoFondosSeleccionado] = useState(null);
  const [cajasFondos, setCajasFondos] = useState([]);
  const [carteraFondos, setCarteraFondos] = useState([]);
  const [cuentasBancariasFondos, setCuentasBancariasFondos] = useState([]);
  const [fondosDetallesCajas, setFondosDetallesCajas] = useState([]);
  const [fondosDetallesCheques, setFondosDetallesCheques] = useState([]);
  const [fondosCuentaId, setFondosCuentaId] = useState('');
  const [fondosConcepto, setFondosConcepto] = useState('');
  const [fondosFecha, setFondosFecha] = useState('');
  const [fondosMonto, setFondosMonto] = useState('');
  const [fondosCajaOrigenId, setFondosCajaOrigenId] = useState('');
  const [fondosCajaDestinoId, setFondosCajaDestinoId] = useState('');
  const [fondosCuentaOrigenId, setFondosCuentaOrigenId] = useState('');
  const [fondosCuentaDestinoId, setFondosCuentaDestinoId] = useState('');

  useEffect(() => {
    const hoy = fechaHoy();
    const [y, m] = hoy.split('-');
    setFormData((prev) => {
      if (prev.fecha) return prev;
      return { ...prev, fecha: hoy, mes: String(parseInt(m, 10)), anio: y };
    });
    setFondosFecha((prev) => prev || hoy);
  }, [fechaHoy]);

  const soloUnCliente = Boolean(location.state?.clienteId);
  const clienteFijo = soloUnCliente && clientes.length > 0 ? clientes.find(c => c.id === location.state.clienteId) : null;
  const soloUnProveedor = Boolean(location.state?.proveedorId);
  const proveedorFijo = soloUnProveedor && proveedores.length > 0 ? proveedores.find(p => p.id === Number(location.state.proveedorId) || p.id === location.state.proveedorId) : null;
  const clientesFiltrados = clientes.filter(c => {
    const busqueda = String(filtrosLista.busqueda_cliente || '').trim().toLowerCase();
    if (busqueda) {
      const cuitNorm = String(c.cuit || '').replace(/\D/g, '');
      const busquedaNorm = busqueda.replace(/\D/g, '');
      const matchRazon = (c.razon_social || '').toLowerCase().includes(busqueda);
      const matchCuit = busquedaNorm ? cuitNorm.includes(busquedaNorm) : false;
      if (!matchRazon && !matchCuit) return false;
    }
    if (filtrosLista.cuit?.trim()) {
      const cuitBusqueda = String(filtrosLista.cuit).replace(/\D/g, '');
      const cuitCliente = String(c.cuit || '').replace(/\D/g, '');
      if (!cuitCliente.includes(cuitBusqueda)) return false;
    }
    return true;
  });
  const proveedoresFiltrados = proveedores.filter(p => {
    const busqueda = String(filtrosLista.busqueda_proveedor || '').trim().toLowerCase();
    if (busqueda) {
      const cuitNorm = String(p.cuit || '').replace(/\D/g, '');
      const busquedaNorm = busqueda.replace(/\D/g, '');
      const matchRazon = (p.razon_social || '').toLowerCase().includes(busqueda);
      const matchCuit = busquedaNorm ? cuitNorm.includes(busquedaNorm) : false;
      if (!matchRazon && !matchCuit) return false;
    }
    if (filtrosLista.cuit?.trim()) {
      const cuitBusqueda = String(filtrosLista.cuit).replace(/\D/g, '');
      const cuitProv = String(p.cuit || '').replace(/\D/g, '');
      if (!cuitProv.includes(cuitBusqueda)) return false;
    }
    return true;
  });
  const esAbonoMensual = movimientoPredefinidoSeleccionado?.descripcion === 'Abono Mensual' && movimientoPredefinidoSeleccionado?.tipo_movimiento === 'DEBITO';
  const tipoClienteFijo = clienteFijo && tipos.length > 0 ? tipos.find(t => t.id === clienteFijo.tipo_id) : null;
  const valorAbonoCliente = clienteFijo && (clienteFijo.abono_mensual != null ? parseFloat(clienteFijo.abono_mensual) : (clienteFijo.valor_abono != null ? parseFloat(clienteFijo.valor_abono) : (tipoClienteFijo?.valor_abono != null ? parseFloat(tipoClienteFijo.valor_abono) : 0)));
  const puedeAplicarAbono = esAbonoMensual && soloUnCliente ? (Number.isFinite(valorAbonoCliente) && valorAbonoCliente > 0) : true;
  const loadClientes = useCallback(async () => {
    try {
      const response = await clientesAPI.getAll({ activo: true });
      if (response.success) {
        setClientes(response.data);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  }, []);

  const loadTipos = useCallback(async () => {
    try {
      const response = await tiposClientesAPI.getAll();
      if (response.success) {
        setTipos(response.data);
      }
    } catch (error) {
      console.error('Error al cargar tipos:', error);
    }
  }, []);

  const loadFormasPago = useCallback(async (tipoMov = null) => {
    try {
      const t = (tipoMov || tipoMovimientoSeleccionado || '').toUpperCase();
      let tipo_movimiento = 'ingreso';
      if (t === 'DEBITO') tipo_movimiento = 'egreso';
      const response = await formasPagoAPI.getDisponibles({ tipo_movimiento });
      if (response.success) {
        setFormasPago(response.data);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  }, [tipoMovimientoSeleccionado]);

  // Formas de pago habilitadas según tipo de movimiento: CRÉDITO = ingreso/ambos, DÉBITO = egreso/ambos
  const formasPagoHabilitadas = useMemo(() => {
    const t = (tipoMovimientoSeleccionado || '').toUpperCase();
    if (!t) return formasPago;
    if (t === 'CREDITO') {
      return formasPago.filter(f => {
        const tm = (f.tipo_movimiento || '').toLowerCase();
        const nombre = (f.nombre || '').toLowerCase();
        const esIngreso = tm === 'ingreso' || tm === 'ambos';
        const esChequePropio = nombre.includes('cheque propio');
        return esIngreso && !esChequePropio;
      });
    }
    if (t === 'DEBITO') {
      return formasPago.filter(f => {
        const tm = (f.tipo_movimiento || '').toLowerCase();
        return tm === 'egreso' || tm === 'ambos';
      });
    }
    return formasPago;
  }, [formasPago, tipoMovimientoSeleccionado]);

  const loadBancos = useCallback(async () => {
    try {
      const response = await bancosAPI.getAll({ activo: true });
      if (response.success) {
        setBancos(response.data);
      }
    } catch (error) {
      console.error('Error al cargar bancos:', error);
    }
  }, []);

  const loadCuentasBancarias = useCallback(async () => {
    try {
      const response = await cuentasBancariasAPI.getAll({ activo: true });
      if (response.success) {
        setCuentasBancarias(response.data);
      }
    } catch (error) {
      console.error('Error al cargar cuentas bancarias:', error);
    }
  }, []);

  const loadProveedores = useCallback(async () => {
    try {
      const response = await proveedoresAPI.getAll({ activo: true });
      if (response.success) {
        setProveedores(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  }, []);

  const loadMovimientosPredefinidos = useCallback(async () => {
    try {
      const params = { activo: true };
      params.uso = (location.state?.nuevoPago || location.state?.modoProveedores) ? 'proveedores' : 'clientes';

      // Si hay clientes seleccionados, obtener sus tipos de cliente
      if (clientesSeleccionados.length > 0 && !modoMasivo) {
        const tiposClientesSeleccionados = clientesSeleccionados.map(clienteId => {
          const cliente = clientes.find(c => c.id === clienteId);
          return cliente?.tipo_id;
        }).filter(Boolean);
        
        // Cargar movimientos predefinidos para esos tipos o sin tipo específico
        if (tiposClientesSeleccionados.length > 0) {
          // Cargar todos los movimientos predefinidos (los filtramos en el frontend)
          params.tipo_cliente_id = tiposClientesSeleccionados[0]; // Por ahora usamos el primero
        }
      } else if (modoMasivo && formData.tipo_id) {
        params.tipo_cliente_id = formData.tipo_id;
      }

      const response = await movimientosPredefinidosAPI.getAll(params);
      if (response.success) {
        // Filtrar en el frontend para mostrar solo los relevantes
        let movimientosFiltrados = response.data;
        
        if (clientesSeleccionados.length > 0 && !modoMasivo) {
          const tiposClientesSeleccionados = clientesSeleccionados.map(clienteId => {
            const cliente = clientes.find(c => c.id === clienteId);
            return cliente?.tipo_id;
          }).filter(Boolean);
          
          movimientosFiltrados = response.data.filter(mov => 
            !mov.tipo_cliente_id || tiposClientesSeleccionados.includes(mov.tipo_cliente_id)
          );
        }
        
        // Excluir Abono Mensual solo cuando NO estamos en modo "un solo cliente" (desde listado de clientes)
        const desdeListadoUnCliente = location.state?.clienteId && clientesSeleccionados.length === 1;
        const sinAbonoMensual = movimientosFiltrados.filter(
          (m) => desdeListadoUnCliente ? true : !(m.descripcion === 'Abono Mensual' && m.tipo_movimiento === 'DEBITO')
        );
        setMovimientosPredefinidos(filtrarPredefinidosPermitidos(filtrarPredefinidosFormulario(sinAbonoMensual)));
      }
    } catch (error) {
      console.error('Error al cargar movimientos predefinidos:', error);
    }
  }, [clientesSeleccionados, modoMasivo, formData.tipo_id, clientes, location.state?.clienteId, location.state?.nuevoPago, location.state?.modoProveedores, filtrarPredefinidosPermitidos]);

  useEffect(() => {
    loadClientes();
    loadTipos();
    loadBancos();
    loadCuentasBancarias();
  }, [loadClientes, loadTipos, loadBancos, loadCuentasBancarias]);

  useEffect(() => {
    loadFormasPago();
  }, [loadFormasPago, tipoMovimientoSeleccionado]);

  useEffect(() => {
    if (location.state?.nuevoPago || location.state?.modoProveedores) {
      loadProveedores();
    }
  }, [loadProveedores, location.state?.nuevoPago, location.state?.modoProveedores]);

  useEffect(() => {
    if (movimientoPredefinidoSeleccionado?.uso === 'proveedores') {
      loadProveedores();
    }
  }, [movimientoPredefinidoSeleccionado?.uso, loadProveedores]);

  useEffect(() => {
    setEsModoFondos(Boolean(location.state?.modoFondos));
  }, [location.state?.modoFondos]);

  const loadDatosFondos = useCallback(async () => {
    if (!location.state?.modoFondos) return;
    try {
      const [resPre, resCajas, resCartera, resCb] = await Promise.all([
        movimientosPredefinidosAPI.getAll({ activo: true, uso: 'fondos' }),
        cajasAPI.getAll({ activo: true }),
        recibosCobroAPI.getCarteraCheques({ estado: 'recibido' }),
        cuentasBancariasAPI.getAll({ activo: true })
      ]);
      let listaFondos = [];
      if (resPre.success && Array.isArray(resPre.data)) {
        listaFondos = filtrarPredefinidosPermitidos(filtrarPredefinidosFormulario(resPre.data));
        setPredefinidosFondos(listaFondos);
      }
      if (resCajas.success && Array.isArray(resCajas.data)) setCajasFondos(resCajas.data);
      if (resCartera.success && Array.isArray(resCartera.data)) setCarteraFondos(resCartera.data);
      if (resCb.success && Array.isArray(resCb.data)) setCuentasBancariasFondos(resCb.data);
      const tipoFondos = location.state?.tipoFondos;
      const claseState = location.state?.clase_movimiento;
      if (claseState && listaFondos.length > 0) {
        const preClase = listaFondos.find((m) => m.clase_fondo === claseState);
        if (preClase) setPredefinidoFondosSeleccionado(preClase);
      } else if (tipoFondos && listaFondos.length > 0) {
        const pre = listaFondos.find(m => (tipoFondos === 'ingreso' && m.tipo_movimiento === 'CREDITO') || (tipoFondos === 'egreso' && m.tipo_movimiento === 'DEBITO'));
        if (pre) setPredefinidoFondosSeleccionado(pre);
      }
    } catch (e) {
      console.error('Error cargar datos fondos:', e);
    }
  }, [location.state?.modoFondos, location.state?.tipoFondos, location.state?.clase_movimiento, filtrarPredefinidosPermitidos]);

  useEffect(() => {
    if (!location.state?.modoFondos) return;
    const st = location.state;
    if (st.caja_origen_id) setFondosCajaOrigenId(String(st.caja_origen_id));
    if (st.caja_destino_id) setFondosCajaDestinoId(String(st.caja_destino_id));
    if (st.cuenta_bancaria_origen_id) setFondosCuentaOrigenId(String(st.cuenta_bancaria_origen_id));
    if (st.cuenta_bancaria_destino_id) {
      setFondosCuentaDestinoId(String(st.cuenta_bancaria_destino_id));
      setFondosCuentaId(String(st.cuenta_bancaria_destino_id));
    }
    if (st.cuenta_bancaria_origen_id && !st.cuenta_bancaria_destino_id) {
      setFondosCuentaId(String(st.cuenta_bancaria_origen_id));
    }
  }, [location.state?.modoFondos, location.state?.caja_origen_id, location.state?.caja_destino_id, location.state?.cuenta_bancaria_origen_id, location.state?.cuenta_bancaria_destino_id]);

  useEffect(() => {
    if (location.state?.modoFondos) loadDatosFondos();
  }, [location.state?.modoFondos, loadDatosFondos]);

  // Inicializar filtros desde solapa Movimientos (una sola vez al tener datos)
  const filtrosMovimientosAplicados = useRef(false);
  useEffect(() => {
    const fm = location.state?.filtrosMovimientos;
    if (!fm || filtrosMovimientosAplicados.current) return;
    const next = {};
    if (fm.busqueda_cliente != null) next.busqueda_cliente = String(fm.busqueda_cliente);
    else if (fm.cliente_id && clientes.length > 0) {
      const c = clientes.find(x => x.id === Number(fm.cliente_id));
      if (c) next.busqueda_cliente = c.razon_social || '';
    }
    if (fm.busqueda_proveedor != null) next.busqueda_proveedor = String(fm.busqueda_proveedor);
    else if (fm.proveedor_id && proveedores.length > 0) {
      const p = proveedores.find(x => x.id === Number(fm.proveedor_id));
      if (p) next.busqueda_proveedor = p.razon_social || '';
    }
    if (fm.cuit != null) next.cuit = String(fm.cuit);
    if (Object.keys(next).length > 0) {
      filtrosMovimientosAplicados.current = true;
      setFiltrosLista(prev => ({ ...prev, ...next }));
    }
  }, [location.state?.filtrosMovimientos, clientes, proveedores]);

  // Preseleccionar cliente si se llegó desde el listado de clientes (botón "Nuevo movimiento" o "Nuevo recibo")
  useEffect(() => {
    const clienteId = location.state?.clienteId;
    if (clientes.length > 0 && clienteId && clientes.some(c => c.id === clienteId)) {
      setClientesSeleccionados(prev => (prev.includes(clienteId) ? prev : [clienteId]));
    }
  }, [clientes, location.state?.clienteId]);

  // Preseleccionar y fijar proveedor si se llegó desde lista con un proveedor específico (Nuevo Pago o Nuevo movimiento)
  useEffect(() => {
    const proveedorId = location.state?.proveedorId;
    if ((location.state?.nuevoPago || location.state?.modoProveedores) && proveedorId && proveedores.length > 0) {
      const id = Number(proveedorId);
      if (proveedores.some(p => p.id === id)) {
        setProveedoresSeleccionados(prev => (prev.length === 1 && prev[0] === id ? prev : [id]));
      }
    }
  }, [proveedores, location.state?.proveedorId, location.state?.nuevoPago, location.state?.modoProveedores]);


  // Preseleccionar "Recibo de Cobro" si se llegó desde "Nuevo recibo" (desde Movimientos o desde lista de clientes)
  useEffect(() => {
    const nuevoRecibo = location.state?.nuevoRecibo;
    const clienteId = location.state?.clienteId;
    if (!nuevoRecibo || movimientosPredefinidos.length === 0 || preseleccionReciboAplicada.current) return;
    const reciboCobro = movimientosPredefinidos.find(
      m => m.tipo_movimiento === 'CREDITO' && (m.descripcion || '').toLowerCase().includes('recibo de cobro')
    );
    if (reciboCobro) {
      const cliente = clienteId ? clientes.find(c => c.id === Number(clienteId) || c.id === clienteId) : null;
      const montoInicial = (cliente != null && (cliente.saldo != null && cliente.saldo !== ''))
        ? String(parseFloat(cliente.saldo))
        : ((reciboCobro.monto != null && reciboCobro.monto !== '') ? reciboCobro.monto : '');
      setTipoMovimientoSeleccionado('CREDITO');
      setMovimientoPredefinidoSeleccionado(reciboCobro);
      setFormData(prev => ({
        ...prev,
        concepto: reciboCobro.descripcion,
        monto: montoInicial
      }));
      preseleccionReciboAplicada.current = true;
      toast.success('Recibo de Cobro seleccionado. Complete monto y formas de pago.');
    } else if (nuevoRecibo) {
      preseleccionReciboAplicada.current = true;
      toast.warning('No tiene permiso para usar Recibo de Cobro o no está disponible.');
    }
  }, [movimientosPredefinidos, clientes, location.state?.nuevoRecibo, location.state?.clienteId]);

  const preseleccionPagoAplicada = useRef(false);
  useEffect(() => {
    const nuevoPago = location.state?.nuevoPago;
    if (!nuevoPago || movimientosPredefinidos.length === 0 || preseleccionPagoAplicada.current) return;
    const pagosPredef = movimientosPredefinidos.find(
      (m) => m.tipo_movimiento === 'DEBITO' && esPredefinidoOrdenPago(m)
    );
    if (pagosPredef) {
      setTipoMovimientoSeleccionado('DEBITO');
      setMovimientoPredefinidoSeleccionado(pagosPredef);
      setFormData(prev => ({ ...prev, concepto: pagosPredef.descripcion }));
      preseleccionPagoAplicada.current = true;
      toast.success('Orden de pago seleccionada. Complete monto, proveedor y formas de pago.');
    } else if (nuevoPago) {
      preseleccionPagoAplicada.current = true;
      toast.warning('No tiene permiso para usar Orden de Pago o no está disponible.');
    }
  }, [movimientosPredefinidos, location.state?.nuevoPago]);

  const preseleccionPagoCuentaAplicada = useRef(false);
  useEffect(() => {
    const nuevoPagoCliente = location.state?.nuevoPagoCliente;
    if (!nuevoPagoCliente || movimientosPredefinidos.length === 0 || preseleccionPagoCuentaAplicada.current) return;
    const prePagoCuenta = movimientosPredefinidos.find(
      (m) => m.tipo_movimiento === 'DEBITO' && esPredefinidoPagoCuenta(m)
    );
    if (prePagoCuenta) {
      setTipoMovimientoSeleccionado('DEBITO');
      setMovimientoPredefinidoSeleccionado(prePagoCuenta);
      setFormData(prev => ({ ...prev, concepto: prePagoCuenta.descripcion }));
      preseleccionPagoCuentaAplicada.current = true;
      toast.success('Pago a cuenta seleccionado. Complete monto, cliente y formas de pago.');
    } else if (nuevoPagoCliente) {
      preseleccionPagoCuentaAplicada.current = true;
      toast.warning('No tiene permiso para usar Pago a cuenta o no está disponible.');
    }
  }, [movimientosPredefinidos, location.state?.nuevoPagoCliente]);

  const huerfanaOpAplicada = useRef(false);
  useEffect(() => {
    const movId = location.state?.movimientoDebitoId;
    if (!movId || huerfanaOpAplicada.current) return;
    huerfanaOpAplicada.current = true;
    if (location.state?.proveedorId) {
      setProveedoresSeleccionados([Number(location.state.proveedorId)]);
    }
    if (location.state?.clienteId) {
      setClientesSeleccionados([Number(location.state.clienteId)]);
    }
    setFormData((prev) => ({
      ...prev,
      monto: location.state?.montoHuerfano != null ? String(location.state.montoHuerfano) : prev.monto,
      concepto: location.state?.conceptoHuerfano || prev.concepto,
      fecha: location.state?.fechaHuerfana ? String(location.state.fechaHuerfana).slice(0, 10) : prev.fecha,
    }));
    toast.info(location.state?.nuevoPagoCliente
      ? 'Complete las formas de pago para registrar el pago a cuenta pendiente.'
      : 'Complete las formas de pago para registrar la orden de pago pendiente.');
  }, [location.state?.movimientoDebitoId, location.state?.proveedorId, location.state?.clienteId, location.state?.montoHuerfano, location.state?.conceptoHuerfano, location.state?.fechaHuerfana, location.state?.nuevoPagoCliente]);

  // En modo "Nuevo recibo", cargar el monto inicial con el saldo actual del cliente (una vez)
  useEffect(() => {
    if (!location.state?.nuevoRecibo || !clienteFijo || saldoInicialReciboAplicado.current) return;
    saldoInicialReciboAplicado.current = true;
    const saldoNum = parseFloat(clienteFijo.saldo);
    setFormData(prev => ({
      ...prev,
      monto: Number.isFinite(saldoNum) ? String(saldoNum) : (prev.monto || '')
    }));
  }, [location.state?.nuevoRecibo, clienteFijo]);

  useEffect(() => {
    if (clientesSeleccionados.length > 0 && !modoMasivo) {
      loadMovimientosPredefinidos();
    } else if (modoMasivo && formData.tipo_id) {
      loadMovimientosPredefinidos();
    } else {
      loadMovimientosPredefinidos();
    }
  }, [clientesSeleccionados, modoMasivo, formData.tipo_id, loadMovimientosPredefinidos]);

  const esPagoPredefinido = movimientoPredefinidoSeleccionado && esPredefinidoPagoComprobante(movimientoPredefinidoSeleccionado);
  useEffect(() => {
    if (!esPagoPredefinido || tipoMovimientoSeleccionado !== 'DEBITO') return;
    recibosCobroAPI.getCarteraCheques().then((res) => {
      if (res.success && Array.isArray(res.data)) setCarteraCheques(res.data);
      else setCarteraCheques([]);
    }).catch(() => setCarteraCheques([]));
  }, [esPagoPredefinido, tipoMovimientoSeleccionado]);

  const abrirModalCartera = (index) => {
    const fp = formasPagoRecibo[index] || {};
    setModalCarteraIndex(index);
    setCarteraSeleccionados(fp.cheque_cartera_ids?.length ? [...fp.cheque_cartera_ids] : (fp.cheque_cartera_id ? [fp.cheque_cartera_id] : []));
    setModalCarteraOpen(true);
    recibosCobroAPI.getCarteraCheques({ estado: 'recibido' }).then((res) => {
      if (res.success && Array.isArray(res.data)) setCarteraRecibidos(res.data);
      else setCarteraRecibidos([]);
    }).catch(() => setCarteraRecibidos([]));
  };

  const confirmarModalCartera = () => {
    if (modalCarteraIndex == null || carteraSeleccionados.length === 0) {
      setModalCarteraOpen(false);
      setModalCarteraIndex(null);
      return;
    }
    const cheques = carteraRecibidos.filter(ch => carteraSeleccionados.includes(ch.id));
    const total = cheques.reduce((s, ch) => s + parseFloat(ch.importe || 0), 0);
    const nuevasFormasPago = [...formasPagoRecibo];
    nuevasFormasPago[modalCarteraIndex] = {
      ...nuevasFormasPago[modalCarteraIndex],
      cheque_cartera_ids: carteraSeleccionados,
      cheque_cartera_id: carteraSeleccionados.length === 1 ? carteraSeleccionados[0] : undefined,
      monto: String(total.toFixed(2))
    };
    setFormasPagoRecibo(nuevasFormasPago);
    setModalCarteraOpen(false);
    setModalCarteraIndex(null);
    setCarteraSeleccionados([]);
  };

  const toggleCarteraCheque = (id) => {
    setCarteraSeleccionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCarteraModalSort = (colId) => {
    if (carteraModalSortBy === colId) {
      setCarteraModalSortAsc(prev => !prev);
    } else {
      setCarteraModalSortBy(colId);
      setCarteraModalSortAsc(true);
    }
  };

  const carteraRecibidosOrdenados = useMemo(() => {
    if (!carteraRecibidos.length) return [];
    const col = carteraModalSortBy;
    const asc = carteraModalSortAsc;
    const getVal = (ch) => {
      if (col === 'numero') return (ch.numero || '').toString().toLowerCase();
      if (col === 'fecha_cobro') return ch.fecha_cobro ? new Date(ch.fecha_cobro).getTime() : 0;
      if (col === 'importe') return parseFloat(ch.importe || 0) || 0;
      if (col === 'cliente') return (ch.cliente_razon_social || '').toString().toLowerCase();
      if (col === 'banco') return (ch.cuenta_banco_nombre || '').toString().toLowerCase();
      return '';
    };
    return [...carteraRecibidos].sort((a, b) => {
      const v1 = getVal(a);
      const v2 = getVal(b);
      const cmp = typeof v1 === 'number' && typeof v2 === 'number' ? v1 - v2 : String(v1).localeCompare(String(v2), 'es');
      return asc ? cmp : -cmp;
    });
  }, [carteraRecibidos, carteraModalSortBy, carteraModalSortAsc]);

  const handleMovimientoPredefinidoSelect = (movPredefinido) => {
    if ((movPredefinido?.descripcion || '').trim().toLowerCase() === 'recibo de cobro grupo') {
      navigate('/recibos-grupo/nuevo', { state: { movimientoPredefinidoId: movPredefinido.id } });
      return;
    }
    setTipoMovimientoSeleccionado(movPredefinido.tipo_movimiento);
    setMovimientoPredefinidoSeleccionado(movPredefinido);
    const esAbono = movPredefinido.descripcion === 'Abono Mensual' && movPredefinido.tipo_movimiento === 'DEBITO';
    const d = new Date();
    const mesActual = d.getMonth() + 1;
    const anioActual = d.getFullYear();
    setFormData(prev => ({
      ...prev,
      concepto: movPredefinido.descripcion,
      monto: (movPredefinido.monto != null && movPredefinido.monto !== '') ? movPredefinido.monto : '',
      ...(esAbono && location.state?.clienteId && { mes: String(mesActual), anio: String(anioActual), fecha: `${anioActual}-${String(mesActual).padStart(2, '0')}-01` })
    }));
    if (esAbono) {
      setAbonoPermitirDuplicado(false);
      setAbonoAclaracion('');
    }
    toast.success(`Movimiento "${movPredefinido.descripcion}" seleccionado`);
  };

  const handleTipoChange = (e) => {
    const tipoId = e.target.value;
    setFormData(prev => ({ ...prev, tipo_id: tipoId }));
    
    // Si es carga masiva, sugerir valor_abono del tipo (opcional; si no se indica monto, el backend usará abono de cada cliente)
    if (modoMasivo && tipoId) {
      const tipoSeleccionado = tipos.find(t => t.id === parseInt(tipoId));
      if (tipoSeleccionado && tipoSeleccionado.valor_abono) {
        setFormData(prev => ({ ...prev, monto: tipoSeleccionado.valor_abono }));
      }
    }
  };

  const handleClienteToggle = (clienteId) => {
    setClientesSeleccionados(prev => {
      if (prev.includes(clienteId)) {
        return prev.filter(id => id !== clienteId);
      } else {
        return [...prev, clienteId];
      }
    });
  };

  const handleSelectAll = () => {
    if (clientesSeleccionados.length === clientesFiltrados.length) {
      setClientesSeleccionados(prev => prev.filter(id => !clientesFiltrados.some(c => c.id === id)));
    } else {
      const idsFiltrados = clientesFiltrados.map(c => c.id);
      setClientesSeleccionados(prev => [...new Set([...prev, ...idsFiltrados])]);
    }
  };

  const handleProveedorToggle = (proveedorId) => {
    setProveedoresSeleccionados(prev => {
      if (prev.includes(proveedorId)) {
        return prev.filter(id => id !== proveedorId);
      }
      return [...prev, proveedorId];
    });
  };

  const handleSelectAllProveedores = () => {
    if (proveedoresSeleccionados.length === proveedoresFiltrados.length) {
      setProveedoresSeleccionados(prev => prev.filter(id => !proveedoresFiltrados.some(p => p.id === id)));
    } else {
      const idsFiltrados = proveedoresFiltrados.map(p => p.id);
      setProveedoresSeleccionados(prev => [...new Set([...prev, ...idsFiltrados])]);
    }
  };

  const handleFiltroListaChange = (e) => {
    const { name, value } = e.target;
    setFiltrosLista(prev => ({ ...prev, [name]: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Funciones para manejar formas de pago del recibo
  const agregarFormaPago = () => {
    setFormasPagoRecibo([...formasPagoRecibo, {
      forma_pago_id: '',
      monto: '',
      cheque: {},
      cheque_propio: {},
      transferencia: {},
      tarjeta: {}
    }]);
  };

  const eliminarFormaPago = (index) => {
    setFormasPagoRecibo(formasPagoRecibo.filter((_, i) => i !== index));
  };

  const actualizarFormaPago = (index, field, value) => {
    const nuevasFormasPago = [...formasPagoRecibo];
    nuevasFormasPago[index] = { ...nuevasFormasPago[index], [field]: value };
    if (field === 'monto') {
      const nombreFp = getFormaPagoNombre(nuevasFormasPago[index].forma_pago_id);
      if (nombreFp.includes('transferencia')) {
        if (!nuevasFormasPago[index].transferencia) nuevasFormasPago[index].transferencia = {};
        nuevasFormasPago[index].transferencia = {
          ...nuevasFormasPago[index].transferencia,
          monto: value,
          fecha: nuevasFormasPago[index].transferencia?.fecha || formData.fecha
        };
      }
    }
    if (field === 'forma_pago_id') {
      const formaPagoSel = formasPago.find(f => String(f.id) === String(value));
      const esChequeTipoPropioSel = formaPagoSel?.nombre && esChequeTipoPropio(formaPagoSel.nombre);
      if (esChequeTipoPropioSel) {
        const hoy = fechaHoy();
        const montoActual = nuevasFormasPago[index].monto;
        nuevasFormasPago[index].cheque_propio = {
          ...nuevasFormasPago[index].cheque_propio,
          fecha_emision: nuevasFormasPago[index].cheque_propio?.fecha_emision || hoy,
          importe: nuevasFormasPago[index].cheque_propio?.importe ?? montoActual
        };
      }
      if (formaPagoSel && (formaPagoSel.nombre || '').toLowerCase().includes('transferencia')) {
        const cuentaId = formaPagoSel.cuenta_bancaria_id != null ? formaPagoSel.cuenta_bancaria_id : null;
        if (!nuevasFormasPago[index].transferencia) nuevasFormasPago[index].transferencia = {};
        nuevasFormasPago[index].transferencia = {
          ...nuevasFormasPago[index].transferencia,
          ...(cuentaId != null && { cuenta_bancaria_id: cuentaId }),
          fecha: nuevasFormasPago[index].transferencia?.fecha || formData.fecha,
          monto: nuevasFormasPago[index].transferencia?.monto ?? nuevasFormasPago[index].monto
        };
      }
    }
    setFormasPagoRecibo(nuevasFormasPago);
  };

  const actualizarDetalleFormaPago = (index, tipo, field, value) => {
    const nuevasFormasPago = [...formasPagoRecibo];
    if (!nuevasFormasPago[index][tipo]) {
      nuevasFormasPago[index][tipo] = {};
    }
    nuevasFormasPago[index][tipo] = { ...nuevasFormasPago[index][tipo], [field]: value };
    setFormasPagoRecibo(nuevasFormasPago);
  };

  const getFormaPagoNombre = (formaPagoId) => {
    if (formaPagoId == null || formaPagoId === '') return '';
    const fp = formasPago.find(f => String(f.id) === String(formaPagoId));
    return fp?.nombre?.toLowerCase() || '';
  };

  const monedaDestinoLinea = (fp) => {
    const forma = formasPago.find(f => String(f.id) === String(fp?.forma_pago_id));
    const nombre = (forma?.nombre || '').toLowerCase();
    if (nombre.includes('transferencia')) {
      const cuentaId = fp?.transferencia?.cuenta_bancaria_id ?? forma?.destino_cuenta_id ?? forma?.cuenta_bancaria_id;
      const cuenta = cuentasBancarias.find(c => String(c.id) === String(cuentaId));
      if (cuenta?.moneda) return String(cuenta.moneda).trim().toUpperCase();
    }
    return String(forma?.destino_moneda || 'ARS').trim().toUpperCase() || 'ARS';
  };

  const esDestinoUsd = (fp) => monedaDestinoLinea(fp) === 'USD';

  const puntaCotizacion = tipoMovimientoSeleccionado === 'DEBITO' ? 'compra' : 'venta';

  const textoCotizacionUsd = (fp) => {
    const puntaLabel = puntaCotizacion === 'compra' ? 'compra' : 'venta';
    let textoBna;
    if (cotizacionError) {
      textoBna = `No se pudo leer el BNA. Cargá la cotización de ${puntaLabel}.`;
    } else if (!cotizacionBna) {
      textoBna = 'Buscando dólar oficial BNA…';
    } else {
      const valor = Number(cotizacionBna[puntaCotizacion]);
      const fechaCot = cotizacionBna.fechaCotizacion;
      const fechaPedida = cotizacionBna.fecha;
      const aDma = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
      const cuando = fechaCot && fechaCot !== fechaPedida
        ? `del ${aDma(fechaCot)} (sin cotización el ${aDma(fechaPedida)})`
        : `del ${aDma(fechaCot || fechaPedida)}`;
      const fmt = valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
      textoBna = `BNA ${puntaLabel} ${cuando}: ${fmt}. El total del comprobante suma los pesos.`;
    }
    return textoTasaUsada(fp, {
      puntaLabel,
      textoBna,
      valorBna: cotizacionBna?.[puntaCotizacion],
    });
  };

  const cotizacionVigente = () => {
    if (cotizacionError) {
      const manual = parseFloat(String(cotizacionManual).replace(',', '.'));
      return manual > 0 ? manual : null;
    }
    const valor = Number(cotizacionBna?.[puntaCotizacion]);
    return valor > 0 ? valor : null;
  };

  const sincronizarMontoTransferencia = (linea, montoPesos) => {
    const nombre = getFormaPagoNombre(linea.forma_pago_id);
    if (!nombre.includes('transferencia')) return linea.transferencia;
    return {
      ...(linea.transferencia || {}),
      monto: montoPesos,
      fecha: linea.transferencia?.fecha || formData.fecha
    };
  };

  const aplicarCotizacionALineas = (lineas, cotiz) => {
    if (!(cotiz > 0)) return lineas;
    return lineas.map((fp) => {
      if (!esDestinoUsd(fp)) return fp;
      if (fp.moneda_ingresada === 'AMBOS') return fp;
      const origen = fp.moneda_ingresada === 'USD' ? 'USD' : 'ARS';
      if (origen === 'USD' && fp.monto_usd !== '' && fp.monto_usd != null) {
        const pesos = pesosDesdeUsd(fp.monto_usd, cotiz);
        const monto = pesos == null ? fp.monto : String(pesos);
        return {
          ...fp,
          cotizacion: cotiz,
          monto,
          transferencia: sincronizarMontoTransferencia(fp, monto)
        };
      }
      if (fp.monto !== '' && fp.monto != null) {
        const usd = usdDesdePesos(fp.monto, cotiz);
        return { ...fp, cotizacion: cotiz, monto_usd: usd == null ? fp.monto_usd : String(usd) };
      }
      return { ...fp, cotizacion: cotiz };
    });
  };

  const actualizarMontoUsdLinea = (index, campo, value) => {
    const cotiz = cotizacionVigente();
    setFormasPagoRecibo((prev) => {
      const nuevas = [...prev];
      const actual = aplicarCambioMontoUsd(nuevas[index], campo, value, cotiz);
      actual.transferencia = sincronizarMontoTransferencia(actual, actual.monto);
      nuevas[index] = actual;
      return nuevas;
    });
  };

  const usarBnaEnLinea = (index) => {
    const cotiz = cotizacionVigente();
    if (!(cotiz > 0)) return;
    setFormasPagoRecibo((prev) => {
      const nuevas = [...prev];
      const actual = aplicarBnaALinea(nuevas[index], cotiz);
      actual.transferencia = sincronizarMontoTransferencia(actual, actual.monto);
      nuevas[index] = actual;
      return nuevas;
    });
  };

  const firmaDestinosUsd = formasPagoRecibo
    .map((fp) => `${fp.forma_pago_id || ''}:${fp.transferencia?.cuenta_bancaria_id || ''}:${monedaDestinoLinea(fp)}`)
    .join('|');
  const hayLineaUsd = firmaDestinosUsd.includes('USD');

  useEffect(() => {
    if (!hayLineaUsd || !formData.fecha) return undefined;
    let cancelado = false;
    cotizacionesAPI.getDolarBna(formData.fecha)
      .then((res) => {
        if (cancelado) return;
        if (res?.success && res.data) {
          setCotizacionBna(res.data);
          setCotizacionError(false);
        } else {
          setCotizacionError(true);
        }
      })
      .catch(() => {
        if (!cancelado) setCotizacionError(true);
      });
    return () => { cancelado = true; };
  }, [hayLineaUsd, formData.fecha]);

  useEffect(() => {
    const cotiz = cotizacionError
      ? parseFloat(String(cotizacionManual).replace(',', '.'))
      : Number(cotizacionBna?.[puntaCotizacion]);
    if (!(cotiz > 0)) return;
    setFormasPagoRecibo((prev) => {
      const next = aplicarCotizacionALineas(prev, cotiz);
      const igual = next.every((fp, i) => (
        String(fp.monto ?? '') === String(prev[i].monto ?? '')
        && String(fp.monto_usd ?? '') === String(prev[i].monto_usd ?? '')
        && Number(fp.cotizacion) === Number(prev[i].cotizacion)
      ));
      return igual ? prev : next;
    });
  }, [cotizacionBna, cotizacionManual, cotizacionError, puntaCotizacion, formasPago, cuentasBancarias]);

  const esChequeTipoPropio = (nombre) => {
    const n = (nombre || '').toLowerCase();
    return n.includes('cheque propio') || n.includes('cheque de tercero');
  };

  /** Unifica monto/fecha/cuenta: el UI puede guardar solo fp.monto y mostrar fecha del recibo sin persistirla en tr. */
  const resolverTransferenciaRecibo = (fp, fechaRecibo, formaPagoObj) => {
    const tr = fp.transferencia || {};
    const montoRaw = tr.monto != null && tr.monto !== '' ? tr.monto : fp.monto;
    return {
      cuenta_bancaria_id: tr.cuenta_bancaria_id ?? formaPagoObj?.cuenta_bancaria_id ?? null,
      fecha: tr.fecha || fechaRecibo || null,
      monto: parseFloat(montoRaw)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      const tipoMovimiento = tipoMovimientoSeleccionado || (modoMasivo ? 'DEBITO' : null);
      
      if (!tipoMovimiento) {
        toast.error('Seleccione un movimiento predefinido o especifique el tipo de movimiento');
        setLoading(false);
        return;
      }

      if (modoMasivo && tipoMovimiento === 'DEBITO') {
        if (!hasPermission('carga_masiva')) {
          toast.error('No tiene permisos para carga masiva');
          setLoading(false);
          return;
        }
        if (!formData.tipo_id) {
          toast.error('Seleccione un tipo de cliente');
          setLoading(false);
          return;
        }
        response = await movimientosAPI.createDebitosMasivos({
          tipo_id: parseInt(formData.tipo_id),
          monto: formData.monto ? parseFloat(formData.monto) : undefined,
          concepto: formData.concepto,
          fecha: formData.fecha
        });
      } else if (tipoMovimiento === 'DEBITO' && soloUnCliente && movimientoPredefinidoSeleccionado?.descripcion === 'Abono Mensual') {
        const clienteId = location.state.clienteId;
        const mes = parseInt(formData.mes, 10) || new Date().getMonth() + 1;
        const anio = parseInt(formData.anio, 10) || new Date().getFullYear();
        const hoy = new Date();
        const fechaHoy = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
        response = await movimientosAPI.createAbonoMensual({
          cliente_ids: [clienteId],
          mes,
          anio,
          fecha: fechaHoy,
          movimiento_predefinido_id: movimientoPredefinidoSeleccionado?.id || null,
          permitir_duplicado: abonoPermitirDuplicado,
          aclaracion: abonoAclaracion.trim() || undefined,
        });
        if (response.success && response.data?.ya_cargados > 0) {
          toast.warning(response.message || 'El cliente ya tiene cargado el abono de ese mes.');
        }
      } else if (tipoMovimiento === 'DEBITO') {
        const esPago = movimientoPredefinidoSeleccionado && esPredefinidoPagoComprobante(movimientoPredefinidoSeleccionado);
        if (esPago) {
          const esPagoProveedor = esPredefinidoOrdenPago(movimientoPredefinidoSeleccionado)
            || esContextoProveedores(location.state, movimientoPredefinidoSeleccionado, proveedoresSeleccionados);
          const esPagoCliente = esPredefinidoPagoCuenta(movimientoPredefinidoSeleccionado)
            || Boolean(location.state?.nuevoPagoCliente);
          if (esPago && !esPagoProveedor && !esPagoCliente) {
            toast.error('Seleccione Orden de Pago o Pago a cuenta');
            setLoading(false);
            return;
          }
          if (esPagoProveedor) {
            if (proveedoresSeleccionados.length === 0) {
              toast.error('Seleccione al menos un proveedor');
              setLoading(false);
              return;
            }
          } else {
            if (clientesSeleccionados.length === 0) {
              toast.error('Seleccione un cliente');
              setLoading(false);
              return;
            }
            if (clientesSeleccionados.length > 1) {
              toast.error('Pago a cuenta permite solo un cliente');
              setLoading(false);
              return;
            }
          }
          if (formasPagoRecibo.length === 0) {
            toast.error('Debe agregar al menos una forma de pago para el pago');
            setLoading(false);
            return;
          }
          const montoTotal = parseFloat(formData.monto || 0);
          if (isNaN(montoTotal) || montoTotal <= 0) {
            toast.error('El monto del pago debe ser mayor a 0');
            setLoading(false);
            return;
          }
          const totalFormasPago = formasPagoRecibo.reduce((sum, fp) => sum + parseFloat(fp.monto || 0), 0);
          if (Math.abs(totalFormasPago - montoTotal) > 0.01) {
            toast.error(`La suma de las formas de pago (${totalFormasPago.toFixed(2)}) debe ser igual al monto total (${montoTotal.toFixed(2)})`);
            setLoading(false);
            return;
          }
          for (let i = 0; i < formasPagoRecibo.length; i++) {
            const fp = formasPagoRecibo[i];
            const nombre = getFormaPagoNombre(fp.forma_pago_id);
            if (!fp.forma_pago_id) {
              toast.error(`Forma de pago ${i + 1}: seleccione una forma de pago`);
              setLoading(false);
              return;
            }
            if (esDestinoUsd(fp)) {
              const usd = parseFloat(fp.monto_usd);
              const cotiz = parseFloat(fp.cotizacion || cotizacionVigente());
              if (!(usd > 0) || !(cotiz > 0)) {
                toast.error(`Forma de pago ${i + 1}: ingresá pesos o dólares y la cotización BNA`);
                setLoading(false);
                return;
              }
            }
            if (esChequeTipoPropio(nombre) && nombre.includes('cheque propio')) {
              const cp = fp.cheque_propio || {};
              if (!cp.numero || !cp.fecha_emision || !cp.cuenta_bancaria_id) {
                toast.error(`Forma de pago ${i + 1} (Cheque Propio): complete número, fecha de emisión y cuenta`);
                setLoading(false);
                return;
              }
            }
            const tieneChequeCartera = (fp.cheque_cartera_ids && fp.cheque_cartera_ids.length) || fp.cheque_cartera_id;
            if (nombre.includes('cheque de tercero') && tipoMovimientoSeleccionado === 'DEBITO' && !tieneChequeCartera) {
              toast.error(`Forma de pago ${i + 1} (Cheque de tercero): seleccione uno o más cheques de la cartera`);
              setLoading(false);
              return;
            }
            if (nombre.includes('transferencia')) {
              const formaPagoObjVal = formasPago.find(f => String(f.id) === String(fp.forma_pago_id));
              const resolved = resolverTransferenciaRecibo(fp, formData.fecha, formaPagoObjVal);
              if (!resolved.cuenta_bancaria_id || !resolved.fecha || isNaN(resolved.monto) || resolved.monto <= 0) {
                toast.error(`Forma de pago ${i + 1} (Transferencia): complete cuenta bancaria, fecha y monto`);
                setLoading(false);
                return;
              }
            }
          }
          const formasPagoConDetalles = formasPagoRecibo.map(fp => {
            const formaPagoObj = formasPago.find(f => String(f.id) === String(fp.forma_pago_id));
            const formaPagoNombre = (formaPagoObj?.nombre || '').toLowerCase();
            const resultado = {
              forma_pago_id: fp.forma_pago_id,
              forma_pago_nombre: formaPagoObj?.nombre ?? null,
              monto: parseFloat(fp.monto),
              ...(esDestinoUsd(fp) ? {
                monto_usd: parseFloat(fp.monto_usd),
                cotizacion: parseFloat(fp.cotizacion || cotizacionVigente())
              } : {})
            };
            const cp = fp.cheque_propio || {};
            const esChequePropioPorNombre = formaPagoNombre.includes('cheque propio') && !formaPagoNombre.includes('cheque de tercero');
            const esChequePropioPorDatos = cp.numero && cp.cuenta_bancaria_id;
            if (esChequePropioPorNombre || (esChequePropioPorDatos && !formaPagoNombre.includes('cheque de tercero'))) {
              resultado.cheque_propio = {
                cuenta_bancaria_id: cp.cuenta_bancaria_id != null ? Number(cp.cuenta_bancaria_id) : null,
                numero: cp.numero ? String(cp.numero).trim() : '',
                fecha_emision: cp.fecha_emision || fechaHoy(),
                fecha_cobro: cp.fecha_cobro || null,
                importe: parseFloat(fp.monto),
                es_electronico: Boolean(cp.es_electronico)
              };
            }
            if (formaPagoNombre.includes('cheque de tercero')) {
              if (fp.cheque_cartera_ids && fp.cheque_cartera_ids.length) {
                resultado.cheque_cartera_ids = fp.cheque_cartera_ids;
              } else if (fp.cheque_cartera_id) {
                resultado.cheque_cartera_id = fp.cheque_cartera_id;
              }
            }
            if (formaPagoNombre.includes('transferencia')) {
              const resolvedTr = resolverTransferenciaRecibo(fp, formData.fecha, formaPagoObj);
              resultado.transferencia = {
                cuenta_bancaria_id: resolvedTr.cuenta_bancaria_id,
                fecha: resolvedTr.fecha,
                monto: resolvedTr.monto
              };
            }
            return resultado;
          });
          if (esPagoProveedor) {
            const numerosPago = [];
            const movimientoDebitoExistente = location.state?.movimientoDebitoId
              ? Number(location.state.movimientoDebitoId)
              : null;
            const proveedoresPago = movimientoDebitoExistente
              ? [Number(location.state?.proveedorId || proveedoresSeleccionados[0])].filter(Boolean)
              : proveedoresSeleccionados;
            if (movimientoDebitoExistente && proveedoresPago.length !== 1) {
              toast.error('La OP huérfana requiere un proveedor');
              setLoading(false);
              return;
            }
            for (const proveedorId of proveedoresPago) {
              let movimientoId = movimientoDebitoExistente;
              if (!movimientoId) {
                const debitoPayload = {
                  monto: parseFloat(formData.monto),
                  concepto: formData.concepto.trim(),
                  fecha: formData.fecha,
                  movimiento_predefinido_id: movimientoPredefinidoSeleccionado?.id || null,
                  proveedor_id: proveedorId
                };
                const debitoResponse = await movimientosAPI.createDebito(debitoPayload);
                if (!debitoResponse.success || !debitoResponse.data?.id) {
                  toast.error(debitoResponse.message || 'Error al crear el débito');
                  setLoading(false);
                  return;
                }
                movimientoId = debitoResponse.data.id;
              }
              const pagoPayload = {
                movimiento_id: movimientoId,
                monto: parseFloat(formData.monto),
                fecha: formData.fecha,
                concepto: formData.concepto.trim(),
                formas_pago: formasPagoConDetalles,
                proveedor_id: proveedorId
              };
              const pagoResponse = await withSaldoNegativoConfirm((confirmar) =>
                pagosAPI.crear({
                  ...pagoPayload,
                  ...(confirmar ? { confirmar_saldo_negativo: true } : {}),
                })
              );
              if (pagoResponse.success && pagoResponse.data?.numero_pago) {
                numerosPago.push(pagoResponse.data.numero_pago);
              } else {
                toast.error(pagoResponse.message || 'Error al crear el pago');
                setLoading(false);
                return;
              }
            }
            if (numerosPago.length > 0) {
              toast.success(numerosPago.length === 1 ? `Pago N° ${numerosPago[0]} creado` : `${numerosPago.length} pagos creados: N° ${numerosPago.join(', ')}`);
              response = { success: true, message: numerosPago.length === 1 ? 'Pago creado exitosamente' : `${numerosPago.length} pagos creados exitosamente` };
            }
          } else {
            const movimientoDebitoExistente = location.state?.movimientoDebitoId
              ? Number(location.state.movimientoDebitoId)
              : null;
            const clienteId = movimientoDebitoExistente
              ? Number(location.state?.clienteId || clientesSeleccionados[0])
              : clientesSeleccionados[0];
            if (!clienteId) {
              toast.error('Seleccione un cliente');
              setLoading(false);
              return;
            }
            let movimientoId = movimientoDebitoExistente;
            if (!movimientoId) {
              const debitoPayload = {
                monto: parseFloat(formData.monto),
                concepto: formData.concepto.trim(),
                fecha: formData.fecha,
                movimiento_predefinido_id: movimientoPredefinidoSeleccionado?.id || null,
                cliente_id: clienteId,
              };
              const debitoResponse = await movimientosAPI.createDebito(debitoPayload);
              if (!debitoResponse.success || !debitoResponse.data?.id) {
                toast.error(debitoResponse.message || 'Error al crear el débito');
                setLoading(false);
                return;
              }
              movimientoId = debitoResponse.data.id;
            }
            const pagoPayload = {
              movimiento_id: movimientoId,
              monto: parseFloat(formData.monto),
              fecha: formData.fecha,
              concepto: formData.concepto.trim(),
              formas_pago: formasPagoConDetalles,
              cliente_id: clienteId,
            };
            const pagoResponse = await withSaldoNegativoConfirm((confirmar) =>
              pagosAPI.crear({
                ...pagoPayload,
                ...(confirmar ? { confirmar_saldo_negativo: true } : {}),
              })
            );
            if (pagoResponse.success) {
              const num = pagoResponse.data.numero_pago_cuenta;
              toast.success(`Pago a cuenta N° ${num} creado`);
              response = { success: true, message: 'Pago a cuenta creado exitosamente' };
            } else {
              toast.error(pagoResponse.message || 'Error al crear el pago');
              setLoading(false);
              return;
            }
          }
        } else if (esContextoProveedores(location.state, movimientoPredefinidoSeleccionado, proveedoresSeleccionados)) {
          // Modo proveedores: desde lista con proveedor fijo o con proveedores seleccionados
          if (proveedoresSeleccionados.length === 0) {
            toast.error('Seleccione al menos un proveedor');
            setLoading(false);
            return;
          }
          if (!formData.monto || parseFloat(formData.monto) <= 0) {
            toast.error('Ingrese un monto mayor a 0');
            setLoading(false);
            return;
          }
          if (!formData.concepto || !formData.concepto.trim()) {
            toast.error('Ingrese el concepto');
            setLoading(false);
            return;
          }
          response = await movimientosAPI.createDebito({
            proveedor_ids: proveedoresSeleccionados,
            monto: parseFloat(formData.monto),
            concepto: formData.concepto.trim(),
            fecha: formData.fecha,
            movimiento_predefinido_id: movimientoPredefinidoSeleccionado?.id || null
          });
        } else {
          if (clientesSeleccionados.length === 0) {
            toast.error('Seleccione al menos un cliente');
            setLoading(false);
            return;
          }
          if (!formData.monto || parseFloat(formData.monto) <= 0) {
            toast.error('Ingrese un monto mayor a 0');
            setLoading(false);
            return;
          }
          if (!formData.concepto || !formData.concepto.trim()) {
            toast.error('Ingrese el concepto');
            setLoading(false);
            return;
          }
          response = await movimientosAPI.createDebito({
            cliente_ids: clientesSeleccionados,
            monto: parseFloat(formData.monto),
            concepto: formData.concepto.trim(),
            fecha: formData.fecha
          });
        }
      } else if (tipoMovimiento === 'CREDITO') {
        const ctxProveedor = esContextoProveedores(
          location.state,
          movimientoPredefinidoSeleccionado,
          proveedoresSeleccionados
        );
        const esReciboDeCobro = movimientoPredefinidoSeleccionado && movimientoPredefinidoSeleccionado.descripcion.toLowerCase().includes('recibo de cobro');

        if (ctxProveedor) {
          if (proveedoresSeleccionados.length === 0) {
            toast.error('Seleccione al menos un proveedor');
            setLoading(false);
            return;
          }
          if (esReciboDeCobro) {
            toast.error('Recibo de cobro no aplica a proveedores');
            setLoading(false);
            return;
          }
          if (!formData.monto || parseFloat(formData.monto) <= 0) {
            toast.error('Ingrese un monto mayor a 0');
            setLoading(false);
            return;
          }
          if (!formData.concepto || !formData.concepto.trim()) {
            toast.error('Ingrese el concepto');
            setLoading(false);
            return;
          }
          response = await movimientosAPI.createCredito({
            proveedor_ids: proveedoresSeleccionados,
            monto: parseFloat(formData.monto),
            concepto: formData.concepto.trim(),
            fecha: formData.fecha,
            movimiento_predefinido_id: movimientoPredefinidoSeleccionado?.id || null
          });
        } else {
          if (clientesSeleccionados.length === 0) {
            toast.error('Seleccione al menos un cliente');
            setLoading(false);
            return;
          }

        // Validar todo el recibo ANTES de crear ningún movimiento (no guardar si falta algo o los montos no coinciden)
        if (esReciboDeCobro) {
          if (formasPagoRecibo.length === 0) {
            toast.error('Debe agregar al menos una forma de pago para el recibo');
            setLoading(false);
            return;
          }
          const montoTotal = parseFloat(formData.monto || 0);
          if (isNaN(montoTotal) || montoTotal <= 0) {
            toast.error('El monto del recibo debe ser mayor a 0');
            setLoading(false);
            return;
          }
          const totalFormasPago = formasPagoRecibo.reduce((sum, fp) => sum + parseFloat(fp.monto || 0), 0);
          if (Math.abs(totalFormasPago - montoTotal) > 0.01) {
            toast.error(`La suma de las formas de pago (${totalFormasPago.toFixed(2)}) debe ser igual al monto total del recibo (${montoTotal.toFixed(2)})`);
            setLoading(false);
            return;
          }
          for (let i = 0; i < formasPagoRecibo.length; i++) {
            const fp = formasPagoRecibo[i];
            const nombre = getFormaPagoNombre(fp.forma_pago_id);
            if (!fp.forma_pago_id) {
              toast.error(`Forma de pago ${i + 1}: seleccione una forma de pago`);
              setLoading(false);
              return;
            }
            if (esDestinoUsd(fp)) {
              const usd = parseFloat(fp.monto_usd);
              const cotiz = parseFloat(fp.cotizacion || cotizacionVigente());
              if (!(usd > 0) || !(cotiz > 0)) {
                toast.error(`Forma de pago ${i + 1}: ingresá pesos o dólares y la cotización BNA`);
                setLoading(false);
                return;
              }
            }
            const fpMonto = parseFloat(fp.monto || 0);
            if (isNaN(fpMonto) || fpMonto <= 0) {
              toast.error(`Forma de pago ${i + 1}: ingrese un monto mayor a 0`);
              setLoading(false);
              return;
            }
            if (esChequeTipoPropio(nombre)) {
              const cp = fp.cheque_propio || {};
              const etiquetaFp = nombre.includes('cheque de tercero') ? 'Cheque de Tercero' : 'Cheque Propio';
              if (!cp.numero || !String(cp.numero).trim()) {
                toast.error(`Forma de pago ${i + 1} (${etiquetaFp}): ingrese el número de cheque`);
                  setLoading(false);
                  return;
                }
              if (!cp.fecha_emision) {
                toast.error(`Forma de pago ${i + 1} (${etiquetaFp}): ingrese la fecha de emisión`);
                  setLoading(false);
                  return;
                }
              if (fpMonto <= 0 || isNaN(fpMonto)) {
                toast.error(`Forma de pago ${i + 1} (${etiquetaFp}): ingrese un monto mayor a 0`);
                  setLoading(false);
                  return;
                }
              if (cp.fecha_cobro && cp.fecha_emision && cp.fecha_cobro < cp.fecha_emision) {
                toast.error(`Forma de pago ${i + 1} (${etiquetaFp}): la fecha de cobro debe ser mayor o igual a la fecha de emisión`);
                  setLoading(false);
                  return;
                }
              if (!nombre.includes('cheque de tercero') && !cp.cuenta_bancaria_id) {
                toast.error(`Forma de pago ${i + 1} (Cheque Propio): seleccione la cuenta bancaria de emisión`);
                setLoading(false);
                return;
              }
              if (nombre.includes('cheque de tercero') && !fp.cheque_cartera_id && !cp.banco_id) {
                toast.error(`Forma de pago ${i + 1} (Cheque de Tercero): seleccione el banco del cheque`);
                setLoading(false);
                return;
              }
            } else if (nombre.includes('cheque') && !esChequeTipoPropio(nombre)) {
              const ch = fp.cheque || {};
              if (!ch.banco_id || !ch.numero_cheque || !String(ch.numero_cheque).trim() || !ch.fecha_emision || !ch.titular || !String(ch.titular).trim()) {
                toast.error(`Forma de pago ${i + 1} (Cheque): complete banco, número, fecha de emisión y titular`);
                setLoading(false);
                return;
              }
            } else if (nombre.includes('transferencia')) {
              const formaPagoObjVal = formasPago.find(f => String(f.id) === String(fp.forma_pago_id));
              const resolved = resolverTransferenciaRecibo(fp, formData.fecha, formaPagoObjVal);
              if (!resolved.cuenta_bancaria_id || !resolved.fecha || isNaN(resolved.monto) || resolved.monto <= 0) {
                toast.error(`Forma de pago ${i + 1} (Transferencia): complete cuenta bancaria, fecha y monto`);
                setLoading(false);
                return;
              }
            } else if (nombre.includes('tarjeta')) {
              if (!fp.tarjeta?.numero_ticket || !String(fp.tarjeta.numero_ticket).trim()) {
                toast.error(`Forma de pago ${i + 1} (Tarjeta): ingrese el número de ticket`);
                setLoading(false);
                return;
              }
            }
          }
        }

        if (esReciboDeCobro) {
          const formasPagoConDetalles = formasPagoRecibo.map(fp => {
            const formaPagoObj = formasPago.find(f => String(f.id) === String(fp.forma_pago_id));
            const formaPagoNombre = formaPagoObj?.nombre?.toLowerCase() || '';

            const resultado = {
              forma_pago_id: fp.forma_pago_id,
              forma_pago_nombre: formaPagoObj?.nombre,
              monto: parseFloat(fp.monto),
              ...(esDestinoUsd(fp) ? {
                monto_usd: parseFloat(fp.monto_usd),
                cotizacion: parseFloat(fp.cotizacion || cotizacionVigente())
              } : {})
            };

            if (esChequeTipoPropio(formaPagoNombre)) {
              const hoy = fechaHoy();
              const esChequeDeTercero = formaPagoNombre.includes('cheque de tercero');
              resultado.cheque_propio = {
                cuenta_bancaria_id: esChequeDeTercero ? null : (fp.cheque_propio?.cuenta_bancaria_id || null),
                banco_id: esChequeDeTercero ? (fp.cheque_propio?.banco_id || null) : null,
                numero: fp.cheque_propio?.numero,
                titular: esChequeDeTercero ? (fp.cheque_propio?.titular || null) : null,
                fecha_emision: fp.cheque_propio?.fecha_emision || hoy,
                fecha_cobro: fp.cheque_propio?.fecha_cobro || null,
                importe: parseFloat(fp.monto),
                es_electronico: Boolean(fp.cheque_propio?.es_electronico)
              };
            } else if (formaPagoNombre.includes('cheque')) {
              resultado.cheque = {
                banco_id: fp.cheque?.banco_id,
                numero_cheque: fp.cheque?.numero_cheque,
                importe: parseFloat(fp.monto),
                fecha_emision: fp.cheque?.fecha_emision,
                fecha_cobro: fp.cheque?.fecha_cobro || null,
                titular: fp.cheque?.titular
              };
            } else if (formaPagoNombre.includes('transferencia')) {
              const resolvedTr = resolverTransferenciaRecibo(fp, formData.fecha, formaPagoObj);
              resultado.transferencia = {
                cuenta_bancaria_id: resolvedTr.cuenta_bancaria_id,
                fecha: resolvedTr.fecha,
                monto: resolvedTr.monto
              };
            } else if (formaPagoNombre.includes('tarjeta')) {
              resultado.tarjeta = {
                numero_ticket: fp.tarjeta?.numero_ticket
              };
            }

            return resultado;
          });

          let creados = 0;
          for (const clienteId of clientesSeleccionados) {
            const reciboResponse = await recibosCobroAPI.crearCompleto({
              cliente_id: clienteId,
              monto: parseFloat(formData.monto),
              fecha: formData.fecha,
              concepto: formData.concepto,
              formas_pago: formasPagoConDetalles,
              movimiento_predefinido_id: movimientoPredefinidoSeleccionado?.id || null,
            });
            if (!reciboResponse.success) {
              throw new Error(reciboResponse.message || 'Error al crear recibo de cobro');
            }
            toast.success(`Recibo de cobro N° ${reciboResponse.data.numero_recibo} creado`);
            creados += 1;
          }
          response = { success: true, message: `${creados} recibo(s) de cobro creado(s) exitosamente` };
        } else {
          const promises = clientesSeleccionados.map(async (clienteId) => {
            return movimientosAPI.createCredito({
              cliente_id: clienteId,
              monto: parseFloat(formData.monto),
              concepto: formData.concepto,
              fecha: formData.fecha,
              forma_pago_id: formData.forma_pago_id || null,
              movimiento_predefinido_id: movimientoPredefinidoSeleccionado?.id || null
            });
          });
          await Promise.all(promises);
          response = { success: true, message: `${clientesSeleccionados.length} crédito(s) creado(s) exitosamente` };
        }
        }
      }

      if (response.success) {
        toast.success(response.message || 'Movimiento creado exitosamente');
        const volverAProveedores =
          location.state?.modoProveedores
          || location.state?.nuevoPago
          || movimientoPredefinidoSeleccionado?.uso === 'proveedores';
        const volverACliente = location.state?.nuevoPagoCliente && location.state?.clienteId;
        if (volverACliente) {
          navigate(`/clientes/${location.state.clienteId}/movimientos`);
        } else {
          navigate(volverAProveedores ? '/movimientos-proveedores' : '/movimientos');
        }
      }
    } catch (error) {
      if (error.cancelled) return;
      const msg = error.response?.data?.message || error.message || 'Error al crear movimiento';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const esModoRecibo = Boolean(location.state?.nuevoRecibo);
  const esModoPago = Boolean(location.state?.nuevoPago);
  const esModoPagoCuenta = Boolean(location.state?.nuevoPagoCliente);
  const esModoProveedores =
    (Boolean(location.state?.modoProveedores) && !esModoPago)
    || movimientoPredefinidoSeleccionado?.uso === 'proveedores';
  const mostrarSelectorProveedor =
    esModoPago || esModoProveedores || movimientoPredefinidoSeleccionado?.uso === 'proveedores';

  const claseFondosActiva = useMemo(() => {
    if (predefinidoFondosSeleccionado?.clase_fondo) return predefinidoFondosSeleccionado.clase_fondo;
    if (location.state?.clase_movimiento) return location.state.clase_movimiento;
    if (location.state?.tipoFondos === 'ingreso') return 'deposito';
    if (location.state?.tipoFondos === 'egreso') return 'extraccion';
    return null;
  }, [predefinidoFondosSeleccionado, location.state?.clase_movimiento, location.state?.tipoFondos]);

  const totalFondosDeposito = useMemo(() => {
    let total = 0;
    fondosDetallesCajas.forEach((d) => {
      const m = parseFloat(d.monto);
      if (!isNaN(m) && m > 0) total += m;
    });
    fondosDetallesCheques.forEach((d) => {
      total += parseFloat(d.monto) || 0;
    });
    return total;
  }, [fondosDetallesCajas, fondosDetallesCheques]);

  const handleSubmitFondos = async (e) => {
    e.preventDefault();
    if (!predefinidoFondosSeleccionado || !fondosConcepto?.trim()) {
      toast.error('Seleccione tipo de movimiento e ingrese concepto.');
      return;
    }
    const clase = claseFondosActiva || (predefinidoFondosSeleccionado.tipo_movimiento === 'CREDITO' ? 'deposito' : 'extraccion');

    setLoading(true);
    try {
      let payload = {
        fecha: fondosFecha,
        concepto: fondosConcepto.trim(),
        movimiento_predefinido_id: predefinidoFondosSeleccionado.id,
      };

      if (clase === 'transferencia_cuenta') {
        if (!fondosCuentaOrigenId || !fondosCuentaDestinoId) {
          toast.error('Seleccione cuenta origen y destino.');
          setLoading(false);
          return;
        }
        const monto = parseFloat(fondosMonto);
        if (isNaN(monto) || monto <= 0) {
          toast.error('Ingrese un monto válido.');
          setLoading(false);
          return;
        }
        payload = {
          ...payload,
          clase_movimiento: 'transferencia_cuenta',
          cuenta_bancaria_origen_id: Number(fondosCuentaOrigenId),
          cuenta_bancaria_destino_id: Number(fondosCuentaDestinoId),
          monto,
        };
      } else if (clase === 'transferencia_caja') {
        if (!fondosCajaOrigenId || !fondosCajaDestinoId) {
          toast.error('Seleccione caja origen y destino.');
          setLoading(false);
          return;
        }
        const monto = parseFloat(fondosMonto);
        if (isNaN(monto) || monto <= 0) {
          toast.error('Ingrese un monto válido.');
          setLoading(false);
          return;
        }
        payload = {
          ...payload,
          clase_movimiento: 'transferencia_caja',
          caja_origen_id: Number(fondosCajaOrigenId),
          caja_destino_id: Number(fondosCajaDestinoId),
          monto,
        };
      } else if (clase === 'deposito') {
        const cuentaDest = fondosCuentaDestinoId || fondosCuentaId;
        if (!cuentaDest) {
          toast.error('Seleccione la cuenta bancaria destino.');
          setLoading(false);
          return;
        }
        const detalles = [];
        fondosDetallesCajas.forEach((d) => {
          const m = parseFloat(d.monto);
          if (!isNaN(m) && m > 0) detalles.push({ tipo: 'caja', caja_id: d.caja_id, monto: m });
        });
        fondosDetallesCheques.forEach((d) => {
          const m = parseFloat(d.monto);
          if (!isNaN(m) && m > 0) detalles.push({ tipo: 'cheque_tercero', recibo_cheque_propio_id: d.recibo_cheque_propio_id, monto: m });
        });
        if (detalles.length === 0 && fondosCajaOrigenId && fondosMonto) {
          const m = parseFloat(fondosMonto);
          if (!isNaN(m) && m > 0) {
            detalles.push({ tipo: 'caja', caja_id: Number(fondosCajaOrigenId), monto: m });
          }
        }
        if (detalles.length === 0) {
          toast.error('Indique caja(s) con monto o cheques de terceros.');
          setLoading(false);
          return;
        }
        payload = {
          ...payload,
          clase_movimiento: 'deposito',
          cuenta_bancaria_destino_id: Number(cuentaDest),
          caja_origen_id: fondosCajaOrigenId ? Number(fondosCajaOrigenId) : detalles.find((d) => d.caja_id)?.caja_id,
          detalles,
        };
      } else {
        const cuentaOrig = fondosCuentaOrigenId || fondosCuentaId;
        if (!cuentaOrig) {
          toast.error('Seleccione la cuenta bancaria origen.');
          setLoading(false);
          return;
        }
        const detalles = [];
        fondosDetallesCajas.forEach((d) => {
          const m = parseFloat(d.monto);
          if (!isNaN(m) && m > 0) detalles.push({ tipo: 'caja', caja_id: d.caja_id, monto: m });
        });
        if (detalles.length === 0 && fondosCajaDestinoId && fondosMonto) {
          const m = parseFloat(fondosMonto);
          if (!isNaN(m) && m > 0) {
            detalles.push({ tipo: 'caja', caja_id: Number(fondosCajaDestinoId), monto: m });
          }
        }
        if (detalles.length === 0) {
          toast.error('Indique caja destino con monto.');
          setLoading(false);
          return;
        }
        payload = {
          ...payload,
          clase_movimiento: 'extraccion',
          cuenta_bancaria_origen_id: Number(cuentaOrig),
          caja_destino_id: fondosCajaDestinoId ? Number(fondosCajaDestinoId) : detalles.find((d) => d.caja_id)?.caja_id,
          detalles,
        };
      }

      const res = await withSaldoNegativoConfirm((confirmar) =>
        fondosAPI.crear({
          ...payload,
          ...(confirmar ? { confirmar_saldo_negativo: true } : {}),
        })
      );
      if (res.success) {
        toast.success(res.message || 'Movimiento de fondos registrado');
        navigate('/movimientos-fondos');
      }
    } catch (err) {
      if (!err.cancelled) toast.error(err.message || 'Error al registrar movimiento de fondos');
    } finally {
      setLoading(false);
    }
  };

  const toggleCajaFondos = (cajaId) => {
    setFondosDetallesCajas(prev => {
      const idx = prev.findIndex(d => d.caja_id === cajaId);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, { caja_id: cajaId, monto: '' }];
    });
  };
  const setMontoCajaFondos = (cajaId, value) => {
    setFondosDetallesCajas(prev => prev.map(d => d.caja_id === cajaId ? { ...d, monto: value } : d));
  };
  const toggleChequeFondos = (ch) => {
    setFondosDetallesCheques(prev => {
      const idx = prev.findIndex(d => d.recibo_cheque_propio_id === ch.id);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, { recibo_cheque_propio_id: ch.id, monto: parseFloat(ch.importe || 0) }];
    });
  };

  const tipoFondosFijo = location.state?.tipoFondos === 'ingreso' || location.state?.tipoFondos === 'egreso' || Boolean(location.state?.clase_movimiento);

  if (esModoFondos) {
    const esTransferencia = claseFondosActiva === 'transferencia_cuenta' || claseFondosActiva === 'transferencia_caja';
    const esDeposito = claseFondosActiva === 'deposito' || (!claseFondosActiva && predefinidoFondosSeleccionado?.tipo_movimiento === 'CREDITO');
    const tituloFondos = tituloMovimientoFondos(claseFondosActiva, location.state?.tipoFondos);
    const subtituloFondos = subtituloMovimientoFondos(claseFondosActiva, location.state?.tipoFondos);
    const etiquetaTipoFijo = predefinidoFondosSeleccionado
      ? (predefinidoFondosSeleccionado.clase_fondo
        ? etiquetaClaseFondo(predefinidoFondosSeleccionado.clase_fondo, predefinidoFondosSeleccionado.tipo_movimiento === 'CREDITO' ? 'ingreso' : 'egreso')
        : predefinidoFondosSeleccionado.descripcion)
      : etiquetaClaseFondo(claseFondosActiva, location.state?.tipoFondos === 'ingreso' ? 'ingreso' : location.state?.tipoFondos === 'egreso' ? 'egreso' : null);

    return (
      <div className="movimiento-form fondos-form">
        <div className="fondos-form-header">
          <h1>{tituloFondos}</h1>
          <p className="page-subtitle">{subtituloFondos}</p>
        </div>
        <form onSubmit={handleSubmitFondos} className="form-container fondos-form-card">
          <div className="form-group">
            <label>Tipo de movimiento *</label>
            {tipoFondosFijo ? (
              <div className={`fondos-tipo-fijo fondos-tipo-fijo--${claseFondosActiva || (location.state?.tipoFondos === 'ingreso' ? 'deposito' : location.state?.tipoFondos === 'egreso' ? 'extraccion' : 'general')}`}>
                {etiquetaTipoFijo}
              </div>
            ) : (
              <select
                value={predefinidoFondosSeleccionado?.id ?? ''}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  setPredefinidoFondosSeleccionado(predefinidosFondos.find(p => p.id === id) || null);
                }}
                required
              >
                <option value="">Seleccione...</option>
                {predefinidosFondos.map(p => (
                  <option key={p.id} value={p.id}>{p.descripcion}</option>
                ))}
              </select>
            )}
          </div>
          <div className="form-group">
            <label>Fecha *</label>
            <input type="date" value={fondosFecha} onChange={(e) => setFondosFecha(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Concepto *</label>
            <input type="text" value={fondosConcepto} onChange={(e) => setFondosConcepto(e.target.value)} required placeholder="Concepto" />
          </div>

          {esTransferencia && (
            <>
              <div className="form-group">
                <label>Monto *</label>
                <input type="number" step="0.01" min="0" value={fondosMonto} onChange={(e) => setFondosMonto(e.target.value)} required placeholder="0.00" />
              </div>
              {claseFondosActiva === 'transferencia_cuenta' && (
                <>
                  <div className="form-group">
                    <label>Cuenta origen *</label>
                    <select value={fondosCuentaOrigenId} onChange={(e) => setFondosCuentaOrigenId(e.target.value)} required>
                      <option value="">Seleccione...</option>
                      {cuentasBancariasFondos.map(cb => (
                        <option key={cb.id} value={cb.id}>{cb.numero_cuenta} {cb.banco_nombre ? `- ${cb.banco_nombre}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Cuenta destino *</label>
                    <select value={fondosCuentaDestinoId} onChange={(e) => setFondosCuentaDestinoId(e.target.value)} required>
                      <option value="">Seleccione...</option>
                      {cuentasBancariasFondos.map(cb => (
                        <option key={cb.id} value={cb.id}>{cb.numero_cuenta} {cb.banco_nombre ? `- ${cb.banco_nombre}` : ''}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {claseFondosActiva === 'transferencia_caja' && (
                <>
                  <div className="form-group">
                    <label>Caja origen *</label>
                    <select value={fondosCajaOrigenId} onChange={(e) => setFondosCajaOrigenId(e.target.value)} required>
                      <option value="">Seleccione...</option>
                      {cajasFondos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Caja destino *</label>
                    <select value={fondosCajaDestinoId} onChange={(e) => setFondosCajaDestinoId(e.target.value)} required>
                      <option value="">Seleccione...</option>
                      {cajasFondos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                </>
              )}
            </>
          )}

          {!esTransferencia && esDeposito && (
            <>
              <div className="form-group">
                <label>Cuenta bancaria destino *</label>
                <select
                  value={fondosCuentaDestinoId || fondosCuentaId}
                  onChange={(e) => { setFondosCuentaDestinoId(e.target.value); setFondosCuentaId(e.target.value); }}
                  required
                >
                  <option value="">Seleccione cuenta...</option>
                  {cuentasBancariasFondos.map(cb => (
                    <option key={cb.id} value={cb.id}>{cb.numero_cuenta} {cb.banco_nombre ? `- ${cb.banco_nombre}` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Cajas origen (seleccione e ingrese monto)</label>
                <div className="fondos-detalle-lista">
                  {cajasFondos.map(c => {
                    const det = fondosDetallesCajas.find(d => d.caja_id === c.id);
                    const selected = !!det;
                    return (
                      <label key={c.id} className="fondos-detalle-item fondos-detalle-item--caja">
                        <input type="checkbox" className="fondos-detalle-check" checked={selected} onChange={() => toggleCajaFondos(c.id)} />
                        <span className="fondos-detalle-texto">{c.nombre}</span>
                        {selected ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="fondos-detalle-monto"
                            placeholder="Monto"
                            value={det.monto}
                            onChange={(e) => setMontoCajaFondos(c.id, e.target.value)}
                          />
                        ) : (
                          <span className="fondos-detalle-monto-placeholder" aria-hidden="true" />
                        )}
                      </label>
                    );
                  })}
                  {cajasFondos.length === 0 && <span className="fondos-empty">No hay cajas activas</span>}
                </div>
              </div>
              <div className="form-group">
                <label>Cheques de terceros (cartera)</label>
                <div className="fondos-detalle-lista">
                  {carteraFondos.map(ch => {
                    const det = fondosDetallesCheques.find(d => d.recibo_cheque_propio_id === ch.id);
                    const selected = !!det;
                    return (
                      <label key={ch.id} className="fondos-detalle-item fondos-detalle-item--cheque">
                        <input type="checkbox" className="fondos-detalle-check" checked={selected} onChange={() => toggleChequeFondos(ch)} />
                        <span className="fondos-detalle-texto" title={`Nº ${ch.numero} · ${ch.cliente_razon_social || '-'}`}>
                          Nº {ch.numero} · {ch.cliente_razon_social || '-'}
                        </span>
                        <span className="fondos-detalle-monto-fijo">
                          ${parseFloat(ch.importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                      </label>
                    );
                  })}
                  {carteraFondos.length === 0 && <span className="fondos-empty">No hay cheques recibidos en cartera</span>}
                </div>
              </div>
              {(fondosDetallesCajas.length > 0 || fondosDetallesCheques.length > 0) && (
                <div className="fondos-total-deposito">
                  Total depósito: <strong>${totalFondosDeposito.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                </div>
              )}
            </>
          )}

          {!esTransferencia && !esDeposito && (
            <>
              <div className="form-group">
                <label>Cuenta bancaria origen *</label>
                <select
                  value={fondosCuentaOrigenId || fondosCuentaId}
                  onChange={(e) => { setFondosCuentaOrigenId(e.target.value); setFondosCuentaId(e.target.value); }}
                  required
                >
                  <option value="">Seleccione cuenta...</option>
                  {cuentasBancariasFondos.map(cb => (
                    <option key={cb.id} value={cb.id}>{cb.numero_cuenta} {cb.banco_nombre ? `- ${cb.banco_nombre}` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Cajas destino (seleccione e ingrese monto)</label>
                <div className="fondos-detalle-lista">
                  {cajasFondos.map(c => {
                    const det = fondosDetallesCajas.find(d => d.caja_id === c.id);
                    const selected = !!det;
                    return (
                      <label key={c.id} className="fondos-detalle-item fondos-detalle-item--caja">
                        <input type="checkbox" className="fondos-detalle-check" checked={selected} onChange={() => toggleCajaFondos(c.id)} />
                        <span className="fondos-detalle-texto">{c.nombre}</span>
                        {selected ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="fondos-detalle-monto"
                            placeholder="Monto"
                            value={det.monto}
                            onChange={(e) => setMontoCajaFondos(c.id, e.target.value)}
                          />
                        ) : (
                          <span className="fondos-detalle-monto-placeholder" aria-hidden="true" />
                        )}
                      </label>
                    );
                  })}
                  {cajasFondos.length === 0 && <span className="fondos-empty">No hay cajas activas</span>}
                </div>
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/movimientos-fondos')} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={loading} className="btn-primary">{loading ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="movimiento-form">
      <h1>{esModoPago ? 'Nuevo Pago' : esModoPagoCuenta ? 'Pago a cuenta' : esModoRecibo ? 'Nuevo Recibo' : 'Nuevo Movimiento'}</h1>
      
      <form onSubmit={handleSubmit} className="form-container">
        {movimientoPredefinidoSeleccionado && (
          <div className="form-group">
            <label>Movimiento Predefinido Seleccionado</label>
            <div style={{
              padding: '14px',
              background: tipoMovimientoSeleccionado === 'DEBITO' ? '#fee' : '#e8f8f5',
              borderRadius: '8px',
              border: `2px solid ${tipoMovimientoSeleccionado === 'DEBITO' ? '#c0392b' : '#27ae60'}`,
              marginBottom: '10px'
            }}>
              <div style={{ 
                color: tipoMovimientoSeleccionado === 'DEBITO' ? '#c0392b' : '#27ae60',
                fontWeight: 'bold',
                fontSize: '16px',
                marginBottom: '6px'
              }}>
                {tipoMovimientoSeleccionado === 'DEBITO' ? 'DÉBITO' : 'CRÉDITO'} - {movimientoPredefinidoSeleccionado.descripcion}
              </div>
              <div style={{ fontSize: '14px', color: '#555' }}>
                Valor: $
                {esAbonoMensual && soloUnCliente && Number.isFinite(valorAbonoCliente)
                  ? valorAbonoCliente.toLocaleString('es-AR', { minimumFractionDigits: 2 }) + ' (según abono del cliente)'
                  : (movimientoPredefinidoSeleccionado.monto != null && movimientoPredefinidoSeleccionado.monto !== '')
                    ? parseFloat(movimientoPredefinidoSeleccionado.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })
                    : '— (completar abajo)'}
                {movimientoPredefinidoSeleccionado.tipo_cliente_nombre && !(esAbonoMensual && soloUnCliente) && (
                  <span style={{ marginLeft: '10px' }}>• Tipo: {movimientoPredefinidoSeleccionado.tipo_cliente_nombre}</span>
                )}
                {esAbonoMensual && soloUnCliente && clienteFijo?.tipo_nombre && (
                  <span style={{ marginLeft: '10px' }}>• Tipo: {clienteFijo.tipo_nombre}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {tipoMovimientoSeleccionado === 'DEBITO' && hasPermission('carga_masiva') && !modoMasivo && !soloUnCliente && !esModoPago && !esModoPagoCuenta && !esModoProveedores && (
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={modoMasivo}
                onChange={(e) => {
                  setModoMasivo(e.target.checked);
                  setClientesSeleccionados([]);
                  setTipoMovimientoSeleccionado(null);
                  setMovimientoPredefinidoSeleccionado(null);
                }}
              />
              Carga Masiva (por tipo de cliente)
            </label>
          </div>
        )}
        
        {!tipoMovimientoSeleccionado && !modoMasivo && (
          <div className="form-group">
            <div style={{
              padding: '12px',
              background: '#fff3cd',
              borderRadius: '6px',
              color: '#856404',
              border: '1px solid #ffeaa7'
            }}>
              <strong>⚠️ Atención:</strong> Debe seleccionar un movimiento predefinido para continuar.
            </div>
          </div>
        )}

        {mostrarSelectorProveedor ? (
          soloUnProveedor && proveedorFijo ? (
            <div className="form-group">
              <label>Proveedor (solo este movimiento)</label>
              <div style={{
                padding: '14px',
                background: '#e8f4f8',
                borderRadius: '8px',
                border: '1px solid #b8d4e0',
                marginTop: '8px'
              }}>
                <div style={{ fontWeight: '600', color: '#2c3e50' }}>{proveedorFijo.razon_social}</div>
                <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
                  CUIT: {proveedorFijo.cuit || '-'}
                  {proveedorFijo.tipo_nombre && (
                    <span style={{ color: '#666' }}> — {proveedorFijo.tipo_nombre}</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
          <>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ marginBottom: '8px', display: 'block', fontWeight: '500', color: '#555' }}>Filtros</label>
              <div className="filters" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '8px' }}>
                <label style={{ whiteSpace: 'nowrap', fontWeight: '500', color: '#555', fontSize: '14px' }}>Proveedor</label>
                <input
                  type="text"
                  name="busqueda_proveedor"
                  value={filtrosLista.busqueda_proveedor}
                  onChange={handleFiltroListaChange}
                  placeholder="Nombre o CUIT"
                  style={{ minWidth: '200px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <label style={{ whiteSpace: 'nowrap', fontWeight: '500', color: '#555', fontSize: '14px', marginLeft: '4px' }}>CUIT</label>
                <input
                  type="text"
                  name="cuit"
                  value={filtrosLista.cuit}
                  onChange={handleFiltroListaChange}
                  placeholder="20-12345678-9"
                  maxLength={13}
                  style={{ width: '13ch', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontVariantNumeric: 'tabular-nums' }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>
                Proveedores * ({proveedoresSeleccionados.length} seleccionado(s))
                <button
                  type="button"
                  onClick={handleSelectAllProveedores}
                  style={{ marginLeft: '10px', padding: '6px 12px', fontSize: '13px' }}
                  className="btn-secondary"
                >
                  {proveedoresSeleccionados.length === proveedoresFiltrados.length && proveedoresFiltrados.length > 0 ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
                </button>
              </label>
              <div style={{
                maxHeight: '250px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '6px',
                padding: '12px',
                marginTop: '8px',
                background: '#fafafa'
              }}>
                {proveedoresFiltrados.map(p => (
                  <label
                    key={p.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      marginBottom: '6px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      background: proveedoresSeleccionados.includes(p.id) ? '#e8f4f8' : 'white'
                    }}
                    onMouseEnter={(e) => {
                      if (!proveedoresSeleccionados.includes(p.id)) e.currentTarget.style.background = '#f0f4f8';
                    }}
                    onMouseLeave={(e) => {
                      if (!proveedoresSeleccionados.includes(p.id)) e.currentTarget.style.background = 'white';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={proveedoresSeleccionados.includes(p.id)}
                      onChange={() => handleProveedorToggle(p.id)}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#2c3e50' }}>{p.razon_social}</div>
                      <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                        {p.cuit} {p.tipo_nombre ? `• ${p.tipo_nombre}` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </>
          )
        ) : soloUnCliente && clienteFijo ? (
          <div className="form-group">
            <label>Cliente (solo este movimiento)</label>
            <div style={{
              padding: '14px',
              background: '#e8f4f8',
              borderRadius: '8px',
              border: '1px solid #b8d4e0',
              marginTop: '8px'
            }}>
              <div style={{ fontWeight: '600', color: '#2c3e50' }}>{clienteFijo.razon_social}</div>
              <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
                CUIT: {clienteFijo.cuit || '-'}
                {!esModoRecibo && clienteFijo.tipo_nombre && (
                  <span style={{ color: '#666' }}>
                    {' '}
                    — {clienteFijo.tipo_nombre}: $
                    {(Number.isFinite(valorAbonoCliente) ? valorAbonoCliente : 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                )}
                {esModoRecibo && (
                  <span style={{ color: '#2c3e50', fontWeight: '600', marginLeft: '12px' }}>
                    Saldo actual: ${parseFloat(clienteFijo.saldo != null ? clienteFijo.saldo : 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                )}
                {esModoPagoCuenta && (
                  <span style={{ color: '#2c3e50', fontWeight: '600', marginLeft: '12px' }}>
                    Saldo actual: ${parseFloat(clienteFijo.saldo != null ? clienteFijo.saldo : 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                )}
              </div>
              {esAbonoMensual && !(valorAbonoCliente > 0) && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#c0392b' }}>Sin valor abono en el tipo de cliente</div>
              )}
            </div>
            {esAbonoMensual && (
              <>
                <p style={{ color: '#666', marginBottom: '12px', marginTop: '16px' }}>
                  Elija el mes y año al que corresponde el abono. Se cargará el monto según el tipo de cliente.
                  {!abonoPermitirDuplicado
                    ? ' Por defecto no se carga dos veces el mismo mes/año a un cliente.'
                    : ' Se permitirá duplicar aunque ya exista abono de ese mes/año.'}
                </p>
                <div className="form-row" style={{ marginBottom: '12px' }}>
                  <div className="form-group">
                    <label>Mes *</label>
                    <select
                      name="mes"
                      value={formData.mes}
                      onChange={handleChange}
                      required
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{MESES_NOMBRES[m - 1]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Año *</label>
                    <input
                      type="number"
                      name="anio"
                      value={formData.anio}
                      onChange={handleChange}
                      min="2000"
                      max="2100"
                      required
                    />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={abonoPermitirDuplicado}
                    onChange={(e) => setAbonoPermitirDuplicado(e.target.checked)}
                  />
                  <span>Permitir duplicar abono del mismo mes/año</span>
                </label>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label>Aclaración (opcional)</label>
                  <input
                    type="text"
                    value={abonoAclaracion}
                    onChange={(e) => setAbonoAclaracion(e.target.value)}
                    placeholder="Texto a agregar al concepto"
                    maxLength={200}
                    style={{ width: '100%', padding: '10px', marginTop: '6px', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>
                <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>
                  Concepto que se registrará: &quot;Abono Mensual - {MESES_NOMBRES[parseInt(formData.mes, 10) - 1] || ''} {formData.anio || ''}
                  {abonoAclaracion.trim() ? ` - ${abonoAclaracion.trim()}` : ''}&quot;.
                  {abonoPermitirDuplicado
                    ? ' Se creará un nuevo débito aunque ya exista abono de ese mes.'
                    : ' Si el cliente ya tiene abono de ese mes se omitirá.'}
                </p>
              </>
            )}
            {!esAbonoMensual && (
              <>
                <div className="form-row" style={{ display: 'flex', flexWrap: 'nowrap', gap: '12px', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ minWidth: '130px' }}>
                    <label>Fecha *</label>
                    <input
                      type="date"
                      name="fecha"
                      value={formData.fecha}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: '100px' }}>
                    <label>Monto *</label>
                    <input
                      type="number"
                      name="monto"
                      value={formData.monto}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  {movimientoPredefinidoSeleccionado && (
                    <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
                      <label>Concepto *</label>
                      {esModoRecibo && movimientoPredefinidoSeleccionado.descripcion.toLowerCase().includes('recibo de cobro') ? (
                        <input
                          type="text"
                          name="concepto"
                          value={formData.concepto}
                          readOnly
                          style={{ background: '#f0f0f0', color: '#555' }}
                          title="Se asignará el número de recibo al guardar"
                        />
                      ) : (
                        <input
                          type="text"
                          name="concepto"
                          value={formData.concepto}
                          onChange={handleChange}
                          required
                          placeholder="Descripción del movimiento"
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : modoMasivo ? (
          <>
            <div className="form-group">
              <label>Tipo de Cliente *</label>
              <select
                name="tipo_id"
                value={formData.tipo_id}
                onChange={handleTipoChange}
                required
              >
                <option value="">Seleccione un tipo</option>
                {tipos.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.nombre} {t.valor_abono ? `($${t.valor_abono})` : ''}
                  </option>
                ))}
              </select>
              {formData.tipo_id && (() => {
                const tipoSeleccionado = tipos.find(t => t.id === parseInt(formData.tipo_id));
                return tipoSeleccionado?.valor_abono ? (
                  <small style={{ color: '#27ae60', marginTop: '5px', display: 'block' }}>
                    Valor abono: ${parseFloat(tipoSeleccionado.valor_abono).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </small>
                ) : null;
              })()}
            </div>
            <div className="form-group">
              <label>Monto (opcional - si no se indica, se usará el abono mensual de cada cliente o del tipo)</label>
              <input
                type="number"
                name="monto"
                value={formData.monto}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Fecha *</label>
              <input
                type="date"
                name="fecha"
                value={formData.fecha}
                onChange={handleChange}
                required
              />
            </div>
          </>
        ) : (
          <>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ marginBottom: '8px', display: 'block', fontWeight: '500', color: '#555' }}>Filtros</label>
              <div className="filters" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '8px' }}>
                <label style={{ whiteSpace: 'nowrap', fontWeight: '500', color: '#555', fontSize: '14px' }}>Cliente</label>
                <input
                  type="text"
                  name="busqueda_cliente"
                  value={filtrosLista.busqueda_cliente}
                  onChange={handleFiltroListaChange}
                  placeholder="Nombre o CUIT"
                  style={{ minWidth: '200px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <label style={{ whiteSpace: 'nowrap', fontWeight: '500', color: '#555', fontSize: '14px', marginLeft: '4px' }}>CUIT</label>
                <input
                  type="text"
                  name="cuit"
                  value={filtrosLista.cuit}
                  onChange={handleFiltroListaChange}
                  placeholder="20-12345678-9"
                  maxLength={13}
                  style={{ width: '13ch', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontVariantNumeric: 'tabular-nums' }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>
                Clientes * ({clientesSeleccionados.length} seleccionado(s))
                <button
                  type="button"
                  onClick={handleSelectAll}
                  style={{ marginLeft: '10px', padding: '6px 12px', fontSize: '13px' }}
                  className="btn-secondary"
                >
                  {clientesSeleccionados.length === clientesFiltrados.length && clientesFiltrados.length > 0 ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
                </button>
              </label>
              <div style={{ 
                maxHeight: '250px', 
                overflowY: 'auto', 
                border: '1px solid #ddd', 
                borderRadius: '6px', 
                padding: '12px',
                marginTop: '8px',
                background: '#fafafa'
              }}>
                {clientesFiltrados.map(c => (
                  <label 
                    key={c.id} 
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      marginBottom: '6px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      background: clientesSeleccionados.includes(c.id) ? '#e8f4f8' : 'white'
                    }}
                    onMouseEnter={(e) => {
                      if (!clientesSeleccionados.includes(c.id)) {
                        e.currentTarget.style.background = '#f0f4f8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!clientesSeleccionados.includes(c.id)) {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={clientesSeleccionados.includes(c.id)}
                      onChange={() => handleClienteToggle(c.id)}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#2c3e50' }}>{c.razon_social}</div>
                      <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                        {c.cuit} {c.tipo_nombre ? `• ${c.tipo_nombre}` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {movimientosPredefinidos.length > 0 && !modoMasivo && !esModoRecibo && !esModoPago && !esModoPagoCuenta && (
          <div className="form-group">
            <label>Movimientos Predefinidos (seleccione uno)</label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '12px',
              marginTop: '10px'
            }}>
              {movimientosPredefinidos.map(mov => (
                <button
                  key={mov.id}
                  type="button"
                  onClick={() => handleMovimientoPredefinidoSelect(mov)}
                  style={{
                    padding: '14px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    textAlign: 'left',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(102, 126, 234, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#ddd';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '6px', color: '#2c3e50', fontSize: '15px' }}>
                    {mov.descripcion}
                  </div>
                  <div style={{ 
                    color: mov.tipo_movimiento === 'DEBITO' ? '#e74c3c' : '#27ae60',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '4px'
                  }}>
                    {(soloUnCliente && mov.descripcion === 'Abono Mensual' && mov.tipo_movimiento === 'DEBITO' && Number.isFinite(valorAbonoCliente))
                      ? `$${valorAbonoCliente.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                      : (mov.monto != null && mov.monto !== '')
                        ? `$${parseFloat(mov.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                        : '—'}
                  </div>
                  {mov.tipo_cliente_nombre && (
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                      Tipo: {mov.tipo_cliente_nombre}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {!soloUnCliente && !modoMasivo && (
          <div className="form-row" style={{ display: 'flex', flexWrap: 'nowrap', gap: '12px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: '130px' }}>
              <label>Fecha *</label>
              <input
                type="date"
                name="fecha"
                value={formData.fecha}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group" style={{ minWidth: '100px' }}>
              <label>Monto *</label>
              <input
                type="number"
                name="monto"
                value={formData.monto}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
              />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
              <label>Concepto *</label>
              <input
                type="text"
                name="concepto"
                value={formData.concepto}
                onChange={handleChange}
                required
                placeholder="Descripción del movimiento"
              />
            </div>
          </div>
        )}

        {modoMasivo && (
          <div className="form-group">
            <label>Concepto *</label>
            <input
              type="text"
              name="concepto"
              value={formData.concepto}
              onChange={handleChange}
              required
              placeholder="Descripción del movimiento"
            />
          </div>
        )}

        {((tipoMovimientoSeleccionado === 'CREDITO' && movimientoPredefinidoSeleccionado?.descripcion?.toLowerCase().includes('recibo de cobro')) ||
          (tipoMovimientoSeleccionado === 'DEBITO' && movimientoPredefinidoSeleccionado && esPredefinidoPagoComprobante(movimientoPredefinidoSeleccionado))) && !modoMasivo && movimientoPredefinidoSeleccionado && (
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ margin: 0 }}>
                {tipoMovimientoSeleccionado === 'DEBITO' ? 'Formas de Pago del Pago *' : 'Formas de Pago del Recibo *'}
              </label>
              <button type="button" onClick={agregarFormaPago} className="btn-primary" style={{ padding: '6px 12px', fontSize: '13px' }}>
                + Agregar Forma de Pago
              </button>
            </div>
            
            {formasPagoRecibo.length === 0 && (
              <div style={{ padding: '15px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', marginBottom: '15px' }}>
                Debe agregar al menos una forma de pago. La suma de los montos debe ser igual al monto total.
              </div>
            )}

            {formasPagoRecibo.map((fp, index) => {
              const formaPagoNombre = getFormaPagoNombre(fp.forma_pago_id);
              const formaPagoObj = formasPago.find(f => String(f.id) === String(fp.forma_pago_id));
              const transferenciaCuentaPrefijada = formaPagoObj?.cuenta_bancaria_id && (formaPagoObj?.nombre || '').toLowerCase().includes('transferencia');
              return (
                <div key={index} style={{ 
                  padding: '10px 12px', 
                  border: '1px solid #ddd', 
                  borderRadius: '6px', 
                  marginBottom: '10px',
                  background: '#f9f9f9'
                }}>
                  {/* Una sola línea: Forma de pago, Monto, detalles según tipo y Eliminar */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '8px' }}>
                    <div className="form-group" style={{ marginBottom: 0, minWidth: esChequeTipoPropio(formaPagoNombre) ? '90px' : '140px', maxWidth: esChequeTipoPropio(formaPagoNombre) ? '110px' : '160px' }}>
                      <label style={{ fontSize: '12px' }}>Forma de pago *</label>
                      <select
                        value={fp.forma_pago_id}
                        onChange={(e) => actualizarFormaPago(index, 'forma_pago_id', e.target.value)}
                        required
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                      >
                        <option value="">Seleccione...</option>
                        {formasPagoHabilitadas.map(f => (
                          <option key={f.id} value={f.id}>{f.nombre}</option>
                        ))}
                      </select>
                    </div>

                    {esDestinoUsd(fp) && !formaPagoNombre.includes('transferencia') ? (
                      <>
                        <div className="form-group" style={{ marginBottom: 0, minWidth: '110px', flex: '1 1 110px' }}>
                          <label htmlFor={`fp-pesos-${index}`} style={{ fontSize: '12px' }}>Pesos *</label>
                          <input
                            id={`fp-pesos-${index}`}
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            value={fp.monto ?? ''}
                            onChange={(e) => actualizarMontoUsdLinea(index, 'pesos', e.target.value)}
                            required
                            placeholder="0.00"
                            style={{ padding: '8px', fontSize: '16px', minHeight: '44px' }}
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0, minWidth: '110px', flex: '1 1 110px' }}>
                          <label htmlFor={`fp-usd-${index}`} style={{ fontSize: '12px' }}>Dólares *</label>
                          <input
                            id={`fp-usd-${index}`}
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            value={fp.monto_usd ?? ''}
                            onChange={(e) => actualizarMontoUsdLinea(index, 'dolares', e.target.value)}
                            required
                            placeholder="0.00"
                            style={{ padding: '8px', fontSize: '16px', minHeight: '44px' }}
                          />
                        </div>
                      </>
                    ) : !formaPagoNombre.includes('transferencia') && (
                    <div className="form-group" style={{ marginBottom: 0, minWidth: '75px', maxWidth: '95px' }}>
                      <label style={{ fontSize: '12px' }}>Monto *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={fp.monto ?? ''}
                        onChange={(e) => actualizarFormaPago(index, 'monto', e.target.value)}
                        required
                        placeholder="0.00"
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                      />
                    </div>
                    )}

                    {esChequeTipoPropio(formaPagoNombre) && (
                      <>
                        {(!formaPagoNombre.includes('cheque de tercero') || tipoMovimientoSeleccionado !== 'DEBITO') && (
                          <>
                            {!formaPagoNombre.includes('cheque de tercero') && (
                              <div className="form-group" style={{ minWidth: '120px', marginBottom: 0 }}>
                                <label style={{ fontSize: '12px' }}>Cuenta *</label>
                                <select
                                  value={fp.cheque_propio?.cuenta_bancaria_id ?? ''}
                                  onChange={(e) => actualizarDetalleFormaPago(index, 'cheque_propio', 'cuenta_bancaria_id', e.target.value)}
                                  required
                                  style={{ padding: '4px 6px', fontSize: '13px' }}
                                >
                                  <option value="">Cuenta...</option>
                                  {cuentasBancarias.map(cb => (
                                    <option key={cb.id} value={cb.id}>
                                      {cb.banco_nombre || 'Banco'} - {cb.numero_cuenta}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {formaPagoNombre.includes('cheque de tercero') && (
                              <div className="form-group" style={{ minWidth: '130px', marginBottom: 0 }}>
                                <label style={{ fontSize: '12px' }}>Banco *</label>
                                <select
                                  value={fp.cheque_propio?.banco_id ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '__agregar_banco__') {
                                      const nombre = window.prompt('Nombre del nuevo banco:');
                                      if (nombre && nombre.trim()) {
                                        bancosAPI.create({ nombre: nombre.trim() })
                                          .then((res) => {
                                            if (res.success && res.data?.id) {
                                              setBancos(prev => [...prev, { id: res.data.id, nombre: nombre.trim() }]);
                                              actualizarDetalleFormaPago(index, 'cheque_propio', 'banco_id', res.data.id);
                                            }
                                          })
                                          .catch(() => toast.error('No se pudo crear el banco'));
                                      }
                                      return;
                                    }
                                    actualizarDetalleFormaPago(index, 'cheque_propio', 'banco_id', v);
                                  }}
                                  required
                                  style={{ padding: '4px 6px', fontSize: '13px' }}
                                >
                                  <option value="">Banco...</option>
                                  {bancos.map(b => (
                                    <option key={b.id} value={b.id}>{b.nombre}</option>
                                  ))}
                                  <option value="__agregar_banco__">+ Agregar banco...</option>
                                </select>
                              </div>
                            )}
                            <div className="form-group" style={{ minWidth: '55px', marginBottom: 0 }}>
                              <label style={{ fontSize: '12px' }}>Nro *</label>
                              <input
                                type="text"
                                value={fp.cheque_propio?.numero || ''}
                                onChange={(e) => actualizarDetalleFormaPago(index, 'cheque_propio', 'numero', e.target.value)}
                                required
                                placeholder="Nº"
                                style={{ padding: '4px 6px', fontSize: '13px', width: '60px' }}
                              />
                            </div>
                            <div className="form-group" style={{ minWidth: '95px', marginBottom: 0 }}>
                              <label style={{ fontSize: '12px' }}>F. emisión *</label>
                              <input
                                type="date"
                                value={fp.cheque_propio?.fecha_emision || fechaHoy()}
                                onChange={(e) => actualizarDetalleFormaPago(index, 'cheque_propio', 'fecha_emision', e.target.value)}
                                required
                                style={{ padding: '4px 6px', fontSize: '13px' }}
                              />
                            </div>
                            <div className="form-group" style={{ minWidth: '95px', marginBottom: 0 }}>
                              <label style={{ fontSize: '12px' }}>F. cobro</label>
                              <input
                                type="date"
                                value={fp.cheque_propio?.fecha_cobro || ''}
                                onChange={(e) => actualizarDetalleFormaPago(index, 'cheque_propio', 'fecha_cobro', e.target.value)}
                                min={fp.cheque_propio?.fecha_emision || undefined}
                                style={{ padding: '4px 6px', fontSize: '13px' }}
                              />
                            </div>
                            {formaPagoNombre.includes('cheque de tercero') && tipoMovimientoSeleccionado !== 'DEBITO' && (
                              <div className="form-group" style={{ minWidth: '140px', marginBottom: 0 }}>
                                <label style={{ fontSize: '12px' }}>Titular / librador</label>
                                <input
                                  type="text"
                                  value={fp.cheque_propio?.titular || ''}
                                  onChange={(e) => actualizarDetalleFormaPago(index, 'cheque_propio', 'titular', e.target.value)}
                                  placeholder="Quién firma el cheque"
                                  style={{ padding: '4px 6px', fontSize: '13px', minWidth: '140px' }}
                                />
                              </div>
                            )}
                            <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
                              <label className="form-toggle-wrap" style={{ marginBottom: 0, fontSize: '12px' }}>
                                <span className="toggle-switch-box">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(fp.cheque_propio?.es_electronico)}
                                    onChange={(e) => actualizarDetalleFormaPago(index, 'cheque_propio', 'es_electronico', e.target.checked)}
                                  />
                                  <span className="form-toggle-switch" />
                                </span>
                                <span className="form-toggle-label">Electrónico</span>
                              </label>
                            </div>
                          </>
                        )}
                        {formaPagoNombre.includes('cheque de tercero') && tipoMovimientoSeleccionado === 'DEBITO' && (
                          <div className="form-group" style={{ minWidth: '180px', marginBottom: 0 }}>
                            <label style={{ fontSize: '12px' }}>Cartera *</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => abrirModalCartera(index)}
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                              >
                                {fp.cheque_cartera_ids?.length || fp.cheque_cartera_id ? 'Cambiar' : 'Seleccionar cheques'}
                              </button>
                              {(fp.cheque_cartera_ids?.length || fp.cheque_cartera_id) && (
                                <span style={{ fontSize: '12px', color: '#555' }}>
                                  {fp.cheque_cartera_ids?.length || 1} cheque(s) - ${parseFloat(fp.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Transferencia: misma línea; si la FP tiene cuenta asignada, no pedir selección */}
                    {formaPagoNombre.includes('transferencia') && (
                      <>
                        {transferenciaCuentaPrefijada ? (
                          <span style={{ fontSize: '12px', alignSelf: 'center', color: '#555', marginRight: '8px' }}>
                            Cuenta: {cuentasBancarias.find(cb => String(cb.id) === String(formaPagoObj?.cuenta_bancaria_id))?.numero_cuenta || formaPagoObj?.cuenta_numero || '—'}
                          </span>
                        ) : (
                          <div className="form-group" style={{ minWidth: '130px', marginBottom: 0 }}>
                            <label style={{ fontSize: '12px' }}>Cuenta *</label>
                            <select
                              value={fp.transferencia?.cuenta_bancaria_id || ''}
                              onChange={(e) => actualizarDetalleFormaPago(index, 'transferencia', 'cuenta_bancaria_id', e.target.value)}
                              required
                              style={{ padding: '4px 6px', fontSize: '13px' }}
                            >
                              <option value="">Seleccione...</option>
                              {cuentasBancarias.map(cb => (
                                <option key={cb.id} value={cb.id}>
                                  {cb.numero_cuenta} - {cb.banco_nombre}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="form-group" style={{ minWidth: '95px', marginBottom: 0 }}>
                          <label style={{ fontSize: '12px' }}>Fecha *</label>
                          <input
                            type="date"
                            value={fp.transferencia?.fecha || formData.fecha}
                            onChange={(e) => actualizarDetalleFormaPago(index, 'transferencia', 'fecha', e.target.value)}
                            required
                            style={{ padding: '4px 6px', fontSize: '13px' }}
                          />
                        </div>
                        {esDestinoUsd(fp) ? (
                          <>
                            <div className="form-group" style={{ minWidth: '110px', marginBottom: 0, flex: '1 1 110px' }}>
                              <label htmlFor={`tr-pesos-${index}`} style={{ fontSize: '12px' }}>Pesos *</label>
                              <input
                                id={`tr-pesos-${index}`}
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={fp.monto ?? ''}
                                onChange={(e) => actualizarMontoUsdLinea(index, 'pesos', e.target.value)}
                                required
                                style={{ padding: '8px', fontSize: '16px', minHeight: '44px' }}
                              />
                            </div>
                            <div className="form-group" style={{ minWidth: '110px', marginBottom: 0, flex: '1 1 110px' }}>
                              <label htmlFor={`tr-usd-${index}`} style={{ fontSize: '12px' }}>Dólares *</label>
                              <input
                                id={`tr-usd-${index}`}
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={fp.monto_usd ?? ''}
                                onChange={(e) => actualizarMontoUsdLinea(index, 'dolares', e.target.value)}
                                required
                                style={{ padding: '8px', fontSize: '16px', minHeight: '44px' }}
                              />
                            </div>
                          </>
                        ) : (
                        <div className="form-group" style={{ minWidth: '75px', marginBottom: 0 }}>
                          <label style={{ fontSize: '12px' }}>Monto *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={fp.transferencia?.monto ?? fp.monto ?? ''}
                            onChange={(e) => {
                              actualizarDetalleFormaPago(index, 'transferencia', 'monto', e.target.value);
                              actualizarFormaPago(index, 'monto', e.target.value);
                            }}
                            required
                            style={{ padding: '4px 6px', fontSize: '13px' }}
                          />
                        </div>
                        )}
                      </>
                    )}

                    {/* Tarjeta: número de ticket en la misma línea */}
                    {formaPagoNombre.includes('tarjeta') && (
                      <div className="form-group" style={{ minWidth: '120px', marginBottom: 0 }}>
                        <label style={{ fontSize: '12px' }}>Nº ticket *</label>
                        <input
                          type="text"
                          value={fp.tarjeta?.numero_ticket || ''}
                          onChange={(e) => actualizarDetalleFormaPago(index, 'tarjeta', 'numero_ticket', e.target.value)}
                          required
                          placeholder="Ticket"
                          style={{ padding: '4px 6px', fontSize: '13px' }}
                        />
                      </div>
                    )}

                    <button type="button" onClick={() => eliminarFormaPago(index)} className="btn-delete" style={{ padding: '4px 8px', fontSize: '12px', marginLeft: 'auto' }}>
                      Eliminar
                    </button>
                  </div>

                  {esDestinoUsd(fp) && (
                    <div style={{ marginTop: '8px' }}>
                      <p style={{ margin: 0, fontSize: '14px', color: '#1a1a1a' }}>{textoCotizacionUsd(fp)}</p>
                      {fp.moneda_ingresada === 'AMBOS' && (
                        <button
                          type="button"
                          onClick={() => usarBnaEnLinea(index)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginTop: '6px',
                            padding: '10px 0',
                            fontSize: '16px',
                            minHeight: '44px',
                            background: 'none',
                            border: 'none',
                            color: '#1565c0',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          Usar BNA
                        </button>
                      )}
                      {cotizacionError && (
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.0001"
                          min="0"
                          aria-label={puntaCotizacion === 'compra' ? 'Cotización BNA compra' : 'Cotización BNA venta'}
                          value={cotizacionManual}
                          onChange={(e) => setCotizacionManual(e.target.value)}
                          placeholder={puntaCotizacion === 'compra' ? 'Compra' : 'Venta'}
                          style={{ marginTop: '6px', padding: '8px', fontSize: '16px', minHeight: '44px', maxWidth: '160px' }}
                        />
                      )}
                    </div>
                  )}

                  {/* Detalles para Cheque (terceros) */}
                  {formaPagoNombre.includes('cheque') && !esChequeTipoPropio(formaPagoNombre) && (
                    <div style={{ marginTop: '15px', padding: '10px', background: 'white', borderRadius: '4px' }}>
                      <strong style={{ display: 'block', marginBottom: '10px' }}>Datos del Cheque:</strong>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Banco *</label>
                          <select
                            value={fp.cheque?.banco_id || ''}
                            onChange={(e) => actualizarDetalleFormaPago(index, 'cheque', 'banco_id', e.target.value)}
                            required
                          >
                            <option value="">Seleccione...</option>
                            {bancos.map(b => (
                              <option key={b.id} value={b.id}>{b.nombre}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Número de Cheque *</label>
                          <input
                            type="text"
                            value={fp.cheque?.numero_cheque || ''}
                            onChange={(e) => actualizarDetalleFormaPago(index, 'cheque', 'numero_cheque', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Importe *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={fp.cheque?.importe || fp.monto || ''}
                            onChange={(e) => {
                              actualizarDetalleFormaPago(index, 'cheque', 'importe', e.target.value);
                              actualizarFormaPago(index, 'monto', e.target.value);
                            }}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Fecha de Emisión *</label>
                          <input
                            type="date"
                            value={fp.cheque?.fecha_emision || ''}
                            onChange={(e) => actualizarDetalleFormaPago(index, 'cheque', 'fecha_emision', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Fecha de Cobro</label>
                          <input
                            type="date"
                            value={fp.cheque?.fecha_cobro || ''}
                            onChange={(e) => actualizarDetalleFormaPago(index, 'cheque', 'fecha_cobro', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Titular *</label>
                          <input
                            type="text"
                            value={fp.cheque?.titular || ''}
                            onChange={(e) => actualizarDetalleFormaPago(index, 'cheque', 'titular', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              );
            })}

            {formasPagoRecibo.length > 0 && (
              <div style={{ 
                padding: '10px', 
                background: formasPagoRecibo.reduce((sum, f) => sum + parseFloat(f.monto || 0), 0) === parseFloat(formData.monto || 0) ? '#d4edda' : '#f8d7da', 
                border: `1px solid ${formasPagoRecibo.reduce((sum, f) => sum + parseFloat(f.monto || 0), 0) === parseFloat(formData.monto || 0) ? '#28a745' : '#dc3545'}`,
                borderRadius: '4px',
                marginTop: '10px'
              }}>
                <strong>Total formas de pago: ${formasPagoRecibo.reduce((sum, f) => sum + parseFloat(f.monto || 0), 0).toFixed(2)}</strong>
                {' · '}
                <span>Monto total: ${parseFloat(formData.monto || 0).toFixed(2)}</span>
                {(parseFloat(formData.monto || 0) - formasPagoRecibo.reduce((sum, f) => sum + parseFloat(f.monto || 0), 0)) > 0.01 && (
                  <>
                    {' · '}
                    <strong style={{ color: '#856404' }}>Faltante: ${(parseFloat(formData.monto || 0) - formasPagoRecibo.reduce((sum, f) => sum + parseFloat(f.monto || 0), 0)).toFixed(2)}</strong>
                  </>
                )}
                {Math.abs(formasPagoRecibo.reduce((sum, f) => sum + parseFloat(f.monto || 0), 0) - parseFloat(formData.monto || 0)) > 0.01 && (
                  <div style={{ color: '#dc3545', marginTop: '5px', fontWeight: 'bold' }}>
                    ⚠️ La suma debe ser igual al monto total
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => {
              if (location.state?.nuevoPagoCliente && location.state?.clienteId) {
                navigate(`/clientes/${location.state.clienteId}/movimientos`);
              } else if (location.state?.modoProveedores || location.state?.nuevoPago) {
                navigate('/movimientos-proveedores');
              } else {
                navigate('/movimientos');
              }
            }}
            className="btn-secondary"
          >
            Cancelar
          </button>
          <button type="submit" disabled={loading || (esAbonoMensual && soloUnCliente && !puedeAplicarAbono)} className="btn-primary">
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>

      {modalCarteraOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => { setModalCarteraOpen(false); setModalCarteraIndex(null); }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '90vw',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Cartera de cheques (estado: recibido)</h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Seleccione uno o más cheques. El monto de la forma de pago será la suma de los importes seleccionados.</p>
            <div style={{ maxHeight: '320px', overflowY: 'auto', marginBottom: '16px', border: '1px solid #ddd', borderRadius: '6px' }}>
              {carteraRecibidos.length === 0 ? (
                <div style={{ padding: '20px', color: '#666' }}>No hay cheques en estado recibido.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}></th>
                      <th
                        style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleCarteraModalSort('numero')}
                        title="Ordenar por Nº"
                      >
                        Nº {carteraModalSortBy === 'numero' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                      </th>
                      <th
                        style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleCarteraModalSort('fecha_cobro')}
                        title="Ordenar por F. cobro"
                      >
                        F. cobro {carteraModalSortBy === 'fecha_cobro' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                      </th>
                      <th
                        style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleCarteraModalSort('importe')}
                        title="Ordenar por Importe"
                      >
                        Importe {carteraModalSortBy === 'importe' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                      </th>
                      <th
                        style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleCarteraModalSort('cliente')}
                        title="Ordenar por Cliente"
                      >
                        Cliente {carteraModalSortBy === 'cliente' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                      </th>
                      <th
                        style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleCarteraModalSort('banco')}
                        title="Ordenar por Banco"
                      >
                        Banco {carteraModalSortBy === 'banco' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {carteraRecibidosOrdenados.map(ch => {
                      const fCobro = ch.fecha_cobro ? formatFecha(ch.fecha_cobro) : '-';
                      return (
                        <tr key={ch.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '8px' }}>
                            <input
                              type="checkbox"
                              checked={carteraSeleccionados.includes(ch.id)}
                              onChange={() => toggleCarteraCheque(ch.id)}
                            />
                          </td>
                          <td style={{ padding: '8px' }}>{ch.numero || '-'}</td>
                          <td style={{ padding: '8px' }}>{fCobro}</td>
                          <td style={{ padding: '8px' }}>${parseFloat(ch.importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '8px' }}>{ch.cliente_razon_social || '-'}</td>
                          <td style={{ padding: '8px' }}>{ch.cuenta_banco_nombre || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" className="btn-secondary" onClick={() => { setModalCarteraOpen(false); setModalCarteraIndex(null); }}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={confirmarModalCartera}
                disabled={carteraSeleccionados.length === 0}
              >
                Confirmar ({carteraSeleccionados.length} seleccionado(s) - ${carteraRecibidos.filter(ch => carteraSeleccionados.includes(ch.id)).reduce((s, ch) => s + parseFloat(ch.importe || 0), 0).toFixed(2)})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovimientoForm;
